import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { normalizeAccessLog, officerLabel } from "../utils/accessLogs";
import { applyBillingEnumText, directionOf, normalizeAccessCode, normalizeDependent, normalizeMaintenanceRequest, splitWords } from "../utils/enums";
import { confirmDialog } from "../components/ConfirmDialog";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, ModalShell, InlineModal, Field, PagePreloader, Avatar, StatusChip, CardHead, TITLE_OPTIONS,
} from "../components/ModalUI";
import { RequestDetailModal } from "../components/AdminMaintenanceModals";

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const prev = getSession() || {};
  const next = { ...prev, ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

async function uploadAdminImage(imageFile, adminId) {
  if (!imageFile || !adminId) return null;
  try {
    const formData = new FormData();
    formData.append("file", imageFile);
    const res = await authFetch(`${API_BASE}/admins/${adminId}/uploads`, {
      method: "POST",
      headers: {},
      body: formData,
    });
    const text = await res.text();
    const json = safeJsonParse(text);
    if (!res.ok) return null;
    if (typeof json?.data !== "string" || !json.data.startsWith("http")) return null;
    return json.data;
  } catch {
    return null;
  }
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function toTitleCase(str) {
  if (!str) return '';
  return String(str).replace(/\w\S*/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
}

function getInitials(name = "") {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateLong(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function fmtMoney(amount) {
  const n = Number(amount || 0);
  return `₦${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function getDuration(start, end) {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : new Date().getTime();
  const diff = Math.max(0, e - s);
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours === 0 && mins === 0) return "Just now";
  if (hours === 0) return `${mins} min${mins > 1 ? "s" : ""}`;
  return `${hours} hr${hours > 1 ? "s" : ""} ${mins} min${mins > 1 ? "s" : ""}`;
}

const VisitProgressBar = ({ checkIn, checkOut, isActive, subjectType, limitAt = null }) => {
  const [elapsed, setElapsed] = useState("");
  const [progressPct, setProgressPct] = useState(0);
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    if (!checkIn) return;
    const start = new Date(checkIn).getTime();

    if (!isActive && checkOut) {
      setElapsed(getDuration(checkIn, checkOut));
      setProgressPct(100);
      return;
    }

    const update = () => {
      const now = new Date().getTime();
      const diff = Math.max(0, now - start);
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);

      let timeStr = "";
      if (hours > 0) timeStr += `${hours} hr${hours > 1 ? "s" : ""} `;
      timeStr += `${mins} min${mins !== 1 ? "s" : ""}`;
      setElapsed(timeStr.trim() || "Just now");

      // Only a temporary pass with a time limit can be overstayed.
      const allowed = limitAt ? Math.max(1, limitAt - start) : 12 * 3600000;
      const pct = (diff / allowed) * 100;
      setProgressPct(Math.min(100, Math.max(5, pct)));
      setIsOverdue(Boolean(isActive && limitAt && now > limitAt));
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [checkIn, checkOut, isActive, subjectType, limitAt]);

  if (!checkIn) return null;

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="fs-7 fw-bold text-gray-600">
          {isActive ? "Duration Tracker" : "Total Duration"}
        </span>
        <span className={`fs-5 fw-bold ${isActive ? (isOverdue ? "text-danger" : "text-success") : "text-success"}`}>
          {elapsed}
        </span>
      </div>
      <div className="progress h-20px bg-gray-200 w-100 rounded-2 overflow-hidden">
        <div
          className={`progress-bar rounded-2 ${
            isActive
              ? isOverdue
                ? "bg-danger progress-bar-striped progress-bar-animated"
                : "bg-success progress-bar-striped progress-bar-animated"
              : "bg-success"
          }`}
          role="progressbar"
          style={{ width: `${isActive ? 100 : progressPct}%`, transition: "width 1s ease" }}
          aria-valuenow={isActive ? 100 : progressPct}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      {isActive && (
        <div className="d-flex align-items-center justify-content-end gap-2 mt-2">
          {isOverdue ? (
            <>
              <i className="ki-outline ki-notification-status text-danger fs-5 animate-pulse" />
              <span className="fs-7 text-danger fw-bold">Pass time limit exceeded</span>
            </>
          ) : (
            <>
              <span className="spinner-grow spinner-grow-sm text-success opacity-75" style={{ width: "0.75rem", height: "0.75rem" }} />
              <span className="fs-7 text-success fw-bold">{subjectType} currently inside</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

function getComponentTypeLabel(type) {
  switch (Number(type)) {
    case 0: return "Rent";
    case 1: return "Service Charge";
    case 2: return "Other";
    default: return "Unknown";
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

const STATUS_META = {
  Created: { badge: "badge-light-danger", icon: "ki-information-5", label: "Open" },
  Open: { badge: "badge-light-danger", icon: "ki-information-5", label: "Open" },
  "In Progress": { badge: "badge-light-warning", icon: "ki-time", label: "In Progress" },
  Resolved: { badge: "badge-light-success", icon: "ki-check-circle", label: "Resolved" },
  Closed: { badge: "badge-light-secondary", icon: "ki-cross-circle", label: "Closed" },
};

const STATUS_TO_INT = {
  "Created": 1, "Open": 1, "In Progress": 2, "Resolved": 3, "Closed": 4,
};

const PRIORITY_META = {
  Low: { badge: "badge-light-info", dot: "#3b82f6" },
  Normal: { badge: "badge-light-secondary", dot: "#94a3b8" },
  High: { badge: "badge-light-warning", dot: "#f59e0b" },
  Critical: { badge: "badge-light-danger", dot: "#ef4444" },
};

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};


function DownloadCarLogModal({ modalRef, cars = [] }) {
  const [searchLog, setSearchLog] = useState("");
  const [makeFilter, setMakeFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");

  const makesList = useMemo(() => {
    const set = new Set();
    cars.forEach(c => {
      const m = c.make || c.manufacturer;
      if (m) set.add(m);
    });
    return Array.from(set);
  }, [cars]);

  const modelsList = useMemo(() => {
    const set = new Set();
    cars.forEach(c => {
      if (c.model) set.add(c.model);
    });
    return Array.from(set);
  }, [cars]);

  const filteredCars = useMemo(() => {
    return cars.filter(c => {
      const title = `${c.manufacturer || ""} ${c.make || ""} ${c.model || ""}`.toLowerCase();
      const plate = (c.licensePlate || "").toLowerCase();
      const drivers = (c.assignedDependents || []).map(d => `${d.firstName || ""} ${d.lastName || ""}`.toLowerCase()).join(" ");
      const q = searchLog.toLowerCase();

      const matchSearch = !q || title.includes(q) || plate.includes(q) || drivers.includes(q);
      const matchMake = !makeFilter || (c.make === makeFilter || c.manufacturer === makeFilter);
      const matchModel = !modelFilter || c.model === modelFilter;

      return matchSearch && matchMake && matchModel;
    });
  }, [cars, searchLog, makeFilter, modelFilter]);

  const handleDownloadReport = () => {
    if (filteredCars.length === 0) {
      toast.error("Car register list is empty. No data to export.");
      return;
    }
    toast.success("Car log report exported successfully!");
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const closeModal = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Car Log"
      onClose={closeModal}
      width={760}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={closeModal}>Close</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownloadReport}>
            <i className="ki-outline ki-file-down fs-5 me-2.5 text-white"></i>Download Report
          </button>
        </>
      }
    >
      <div className="d-flex flex-column gap-4">
        <Field label="Search log">
          <div className="position-relative">
            <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
            <input type="text" className="d6m-input ps-10" placeholder="Search by driver, make, model..." value={searchLog} onChange={(e) => setSearchLog(e.target.value)} />
          </div>
        </Field>
        <div className="row g-4">
          <Field className="col-6" label="Make">
            <select className="d6m-input" value={makeFilter} onChange={(e) => setMakeFilter(e.target.value)}>
              <option value="">All Makes</option>
              {makesList.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field className="col-6" label="Model">
            <select className="d6m-input" value={modelFilter} onChange={(e) => setModelFilter(e.target.value)}>
              <option value="">All Models</option>
              {modelsList.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="d6m-section">
        <div className="d6m-section-head"><span>Car register</span><span className="d6m-count">{filteredCars.length}</span></div>
        {filteredCars.length === 0 ? (
          <div className="d6m-empty">No cars match your filters.</div>
        ) : (
          <div className="d6m-list" style={{ maxHeight: 340, overflowY: "auto" }}>
            {filteredCars.map((c, idx) => (
              <div key={c.id || idx} className="d6m-list-row" style={{ cursor: "default" }}>
                <span className="d6m-muted" style={{ width: 22 }}>{idx + 1}</span>
                <Avatar src={c.imageUrl || getCarImageUrl(c.make, c.model, c.year, c.color)} name={c.make || "Car"} size={40} />
                <span className="min-w-0 flex-grow-1 text-start">
                  <span className="d6m-v d-block text-truncate">
                    {c.manufacturer && c.make && c.manufacturer !== c.make
                      ? `${c.manufacturer} ${c.make}`
                      : (c.make && c.model && c.make !== c.model
                          ? `${c.make} ${c.model}`
                          : (c.manufacturer || c.make || c.model || "Vehicle"))}
                  </span>
                  <span className="d6m-muted d-block text-truncate">
                    {c.year || "—"} · {c.assignedDependents && c.assignedDependents.length > 0
                      ? c.assignedDependents.map((d) => toTitleCase(`${d.firstName || ""} ${d.lastName || ""}`.trim())).join(", ")
                      : "No drivers"}
                  </span>
                </span>
                <StatusChip color="#16A34A">{c.licensePlate || "—"}</StatusChip>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function hideModal(ref) {
  if (window.bootstrap?.Modal && ref.current) window.bootstrap.Modal.getInstance(ref.current)?.hide();
}

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  
  // Normalize key for flexible matching: lowercase, remove non-alphanumeric, normalize bedroom plural/singular
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  
  if (norm.includes("1bedroom") || norm.includes("1bd")) {
    return "/assets/media/zangon/1bedroombungalo.jpg";
  }
  if (norm.includes("2bedroom") || norm.includes("2bd")) {
    return "/assets/media/zangon/2bedroom.jpg";
  }
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) {
    return "/assets/media/zangon/3bedroomsbungalo.jpg";
  }
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) {
    return "/assets/media/zangon/4bedroombungalo.jpg";
  }
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) {
    return "/assets/media/zangon/4bd.jpg";
  }
  if (norm.includes("5bedroom")) {
    const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark" || 
                   localStorage.getItem("kt_theme_mode") === "dark";
    return isDark ? "/assets/media/zangon/5-dark.jpg" : "/assets/media/zangon/5-light.jpg";
  }
  if (norm.includes("6bedroombungalow")) {
    return "/assets/media/zangon/6bedroombungalow.jpg";
  }
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function formatDependentType(type) {
  if (type === undefined || type === null) return "—";
  if (typeof type === "number") {
    switch (type) {
      case 0: return "Family Member";
      case 1: return "Staff";
      case 2: return "Child";
      case 3: return "Domestic Staff";
      case 4: return "Driver";
      case 5: return "Visitor (No Access Code)";
      default: return "Unknown";
    }
  }
  return String(type).replace(/([a-z])([A-Z])/g, "$1 $2");
}

function checkIsBuildingAssigned(building, tenants = []) {
  if (!building) return false;
  if (
    building.isAssigned ||
    building.isOccupied ||
    building.status === "Occupied" ||
    building.status === "Assigned" ||
    building.currentTenantId ||
    building.currentTenantName ||
    building.tenantId ||
    building.tenantName ||
    building.occupantName
  ) {
    return true;
  }
  if (tenants && tenants.length > 0) {
    const bId = building.id || building.buildingId;
    const bNum = String(building.number || building.buildingNumber || "").trim();
    return tenants.some(t =>
      t.buildings?.some(tb => {
        const tbId = tb.id || tb.buildingId;
        const tbNum = String(tb.number || tb.buildingNumber || "").trim();
        return (tbId && bId && String(tbId) === String(bId)) || (tbNum && bNum && tbNum === bNum);
      })
    );
  }
  return false;
}

function sortBuildingsForAssignment(buildingsList, tenants = []) {
  if (!buildingsList || !Array.isArray(buildingsList)) return [];
  return [...buildingsList].sort((a, b) => {
    const isAssignedA = checkIsBuildingAssigned(a, tenants);
    const isAssignedB = checkIsBuildingAssigned(b, tenants);

    if (isAssignedA !== isAssignedB) {
      return isAssignedA ? 1 : -1;
    }

    const phaseA = String(a.phaseName || "");
    const phaseB = String(b.phaseName || "");
    const phaseComp = phaseA.localeCompare(phaseB, undefined, { numeric: true, sensitivity: "base" });
    if (phaseComp !== 0) return phaseComp;

    const numA = String(a.number || a.buildingNumber || "");
    const numB = String(b.number || b.buildingNumber || "");
    return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
  });
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

// ─────────────────────────────────────────────────────────────
//  InvoiceDetailModal
//  Shows invoice details
// ─────────────────────────────────────────────────────────────
function InvoiceDetailModal({ invoice, onClose }) {
  if (!invoice) return null;

  const isPaid = ["Paid"].includes(invoice.status);
  const statusColor = isPaid ? "#16A34A"
    : invoice.status === "Created" ? "#D97706"
      : invoice.status === "Overdue" ? "#DC2626" : "#a1a1aa";
  const total = invoice.totalAmount || invoice.rentAmount;
  const overdue = !isPaid && invoice.dueDate && new Date(invoice.dueDate) < new Date();

  return (
    <InlineModal
      open
      icon="ki-document"
      title="Invoice Details"
      onClose={onClose}
      width={640}
      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={onClose}>Close</button>}
    >
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="d6m-amount">{fmtMoney(total)}</div>
          <div className="d6m-muted mt-1">Invoice total</div>
        </div>
        <StatusChip color={statusColor}>{invoice.status || "—"}</StatusChip>
      </div>

      <div className="d6m-kv d6m-kv-4 mt-5">
        <div className="d6m-kv-cell"><span className="d6m-k">Building</span><span className="d6m-v">{invoice.buildingNumber || "—"}</span></div>
        <div className="d6m-kv-cell"><span className="d6m-k">Rent schedule</span><span className="d6m-v">{splitWords(invoice.rentSchedule || invoice.rentPaymentSchedule) || "—"}</span></div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Period</span>
          <span className="d6m-v">{invoice.periodStart ? fmtDate(invoice.periodStart) : "—"}{invoice.periodEnd ? ` → ${fmtDate(invoice.periodEnd)}` : ""}</span>
        </div>
        <div className="d6m-kv-cell"><span className="d6m-k">Due date</span><span className={`d6m-v ${overdue ? "text-danger" : ""}`}>{fmtDate(invoice.dueDate)}</span></div>
      </div>
      {invoice.paidAt && <div className="d6m-muted mt-3">Paid on <strong className="text-gray-900">{fmtDate(invoice.paidAt)}</strong></div>}

      <div className="d6m-section">
        <div className="d6m-section-head"><span>{invoice.components && invoice.components.length > 0 ? "Invoice components" : "Rent amount (snapshotted)"}</span></div>
        <div className="d6m-invoice">
          <div className="d6m-lines" style={{ marginTop: 0 }}>
            {invoice.components && invoice.components.length > 0 ? (
              invoice.components.map((comp, idx) => (
                <div key={idx} className="d6m-line"><span>{comp.name}</span><span>{fmtMoney(comp.amount)}</span></div>
              ))
            ) : (
              <div className="d6m-line"><span>Rent</span><span>{fmtMoney(invoice.rentAmount)}</span></div>
            )}
            <div className="d6m-line d6m-line-total"><span>Total invoice amount</span><span>{fmtMoney(total)}</span></div>
          </div>
        </div>
      </div>
    </InlineModal>
  );
}

function PaymentReceiptModal({ payment, onClose }) {
  if (!payment) return null;
  const statusColor = ["Successful", "Paid"].includes(payment.status) ? "#16A34A"
    : payment.status === "New" || payment.status === "Pending" ? "#2563EB"
      : payment.status === "Reversed" ? "#D97706" : "#DC2626";

  return (
    <InlineModal
      open
      icon="ki-wallet"
      title="Payment Details"
      onClose={onClose}
      width={640}
      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={onClose}>Close</button>}
    >
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="d6m-amount">{fmtMoney(payment.amountPaid)}</div>
          <div className="d6m-muted mt-1">Amount paid{payment.paidAt ? ` · ${fmtDate(payment.paidAt)}` : ""}</div>
        </div>
        <StatusChip color={statusColor}>{payment.status || "—"}</StatusChip>
      </div>

      <div className="d6m-kv d6m-kv-4 mt-5">
        <div className="d6m-kv-cell"><span className="d6m-k">Transaction charge</span><span className="d6m-v">{fmtMoney(payment.transactionCharge)}</span></div>
        <div className="d6m-kv-cell"><span className="d6m-k">Building</span><span className="d6m-v">{payment.buildingNumber || "—"}</span></div>
        <div className="d6m-kv-cell"><span className="d6m-k">Merchant ref</span><span className="d6m-v text-break">{payment.merchantReference || "—"}</span></div>
        <div className="d6m-kv-cell"><span className="d6m-k">Gateway ref</span><span className="d6m-v text-break">{payment.gatewayReference || "—"}</span></div>
      </div>

      {payment.receiptUrl && (
        <a href={payment.receiptUrl} target="_blank" rel="noreferrer" className="btn btn-success d6-mbtn w-100 mt-5">
          <i className="ki-outline ki-down-square fs-5 me-2.5 text-white"></i>Download Receipt
        </a>
      )}
    </InlineModal>
  );
}

function ViewCarDriversModal({ car, estateId, tenantId, tenant, onClose, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [assignedDependents, setAssignedDependents] = useState([]);

  useEffect(() => {
    if (!car?.id) return;
    let isMounted = true;
    const fetchDrivers = async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/cars/${car.id}`);
        const json = safeJsonParse(await res.text());
        if (!res.ok) throw new Error(json?.message || "Failed to fetch car details.");
        if (isMounted) setAssignedDependents(json?.data?.assignedDependents || []);
      } catch (err) {
        toast.error(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchDrivers();
    return () => { isMounted = false; };
  }, [car?.id, estateId, tenantId]);

  if (!car) return null;

  const ownerName = toTitleCase(`${tenant?.title ? tenant.title + " " : ""}${tenant?.firstName || ""} ${tenant?.lastName || ""}`.trim()) || "Tenant Owner";
  const ownerInitials = getInitials(ownerName);
  const ownerImageUrl = tenant?.imageUrl || tenant?.photoUrl;
  const housesStr = (tenant?.assignedBuildings || []).map(b => `${b.phaseName || b.phase || "Phase"} - ${b.number || b.buildingNumber || b.name}`).join(", ") || "Assigned Property";

  const makeStr = car.make || car.manufacturer || "";
  const modelStr = car.model || "";
  const carName = car.make && car.model && car.make !== car.model
    ? `${car.make} ${car.model}`
    : (car.manufacturer && car.make && car.manufacturer !== car.make
        ? `${car.manufacturer} ${car.make}`
        : (car.make || car.model || car.manufacturer || "Vehicle"));
  const carImgUrl = car.imageUrl || getCarImageUrl(makeStr, modelStr, car.year, car.color) || "/assets/media/zangon/1bedroombungalo.jpg";

  return (
    <InlineModal
      open
      icon="ki-car"
      title="Vehicle Details"
      onClose={onClose}
      width={720}
      footer={
        <div className="d-flex align-items-center justify-content-between w-100 gap-2">
          {onDelete ? (
            <button
              type="button"
              className="btn btn-light-danger d6-mbtn"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                const removed = await onDelete(car.id);
                setDeleting(false);
                if (removed) onClose();
              }}
            >
              {deleting
                ? <><span className="spinner-border spinner-border-sm me-2" />Removing…</>
                : <><i className="ki-outline ki-trash fs-5 me-2" />Remove Car</>}
            </button>
          ) : <span />}
          <button type="button" className="btn btn-light d6-mbtn" onClick={onClose} disabled={deleting}>Close</button>
        </div>
      }
    >
      <div className="d6-card p-4 d-flex align-items-center justify-content-center bg-light border border-gray-200 mb-4" style={{ height: 170 }}>
        <img
          alt={carName}
          src={carImgUrl}
          className="mw-100 mh-100 object-fit-contain"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>
      <div className="d-flex flex-wrap gap-2 mb-5">
        <StatusChip color="#16A34A">{carName}</StatusChip>
        {car.year && <StatusChip color="#a1a1aa">{car.year}</StatusChip>}
        {car.color && <StatusChip color="#2563EB">{car.color}</StatusChip>}
      </div>

      <div className="d6m-person">
        <Avatar src={ownerImageUrl} name={ownerName} size={56} />
        <div className="min-w-0 flex-grow-1">
          <div className="d6m-name text-truncate">{ownerName}</div>
          <div className="d6m-muted text-truncate">{housesStr}</div>
        </div>
      </div>

      <div className="d6m-section">
        <div className="d6m-section-head">
          <span>Authorised drivers</span>
          {!loading && <span className="d6m-count">{assignedDependents.length}</span>}
        </div>
        {loading ? (
          <div className="d-flex flex-column gap-2">
            {[1, 2, 3].map(n => <span key={n} className="d6m-shimmer" style={{ height: 52, borderRadius: 12 }} />)}
          </div>
        ) : assignedDependents.length === 0 ? (
          <div className="d6m-empty">No authorised drivers found for this vehicle.</div>
        ) : (
          <div className="d6m-list">
            {assignedDependents.map((dep, idx) => (
              <div key={dep.dependentId || dep.id || idx} className="d6m-list-row" style={{ cursor: "default" }}>
                <Avatar src={dep.imageUrl} name={dep.fullName || dep.name || "?"} size={38} />
                <span className="min-w-0 flex-grow-1 text-start">
                  <span className="d6m-v d-block text-truncate">{toTitleCase(dep.fullName || dep.name || `${dep.firstName || ""} ${dep.lastName || ""}`.trim())}</span>
                  <span className="d6m-muted d-block text-truncate">{dep.phoneNumber || dep.phone || "—"}</span>
                </span>
                <span className="d6m-muted flex-shrink-0">{dep.assignedAt ? fmtDateTime(dep.assignedAt) : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </InlineModal>
  );
}

function AccessDetailsModal({ person, estateId, tenantId, tenant = {}, onClose, onDelete, onRefreshList }) {
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isPermanent, setIsPermanent] = useState(true);
  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState(0);
  const modalRef = useRef(null);

  const [accessLogs, setAccessLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const fullName = toTitleCase(`${person.firstName || ""} ${person.lastName || ""}`.trim());
  const type = formatDependentType(person.dependentType || person.relationship || person.role);

  useEffect(() => {
    let modalInstance = null;
    if (modalRef.current && window.bootstrap?.Modal) {
      modalInstance = new window.bootstrap.Modal(modalRef.current, { backdrop: "static" });
      modalInstance.show();
      modalRef.current.addEventListener("hidden.bs.modal", () => { if (onClose) onClose(); });
    }
    return () => {
      if (modalInstance) modalInstance.hide();
      document.querySelectorAll(".modal-backdrop").forEach(el => el.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [onClose]);

  const handleClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    } else { if (onClose) onClose(); }
  };

  const fetchAccessCodes = useCallback(async () => {
    setLoadingCodes(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/dependents/${person.id}/access-codes?pageIndex=1&pageSize=10`);
      const json = await res.json();
      setCodes((json?.data?.data || parseArrayData(json) || []).map(normalizeAccessCode));
    } catch (err) { console.error(err); }
    finally { setLoadingCodes(false); }
  }, [estateId, tenantId, person.id]);

  const fetchPersonAccessLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      let logs = [];
      const searchKey = person.firstName || fullName || "";
      if (searchKey) {
        const resSearch = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?search=${encodeURIComponent(searchKey)}&pageIndex=1&pageSize=100`);
        if (resSearch.ok) {
          const jsonSearch = await resSearch.json();
          logs = Array.isArray(jsonSearch?.data?.data) ? jsonSearch.data.data : (parseArrayData(jsonSearch) || []);
        }
      }
      if (!logs || logs.length === 0) {
        const qp = new URLSearchParams();
        qp.append("pageIndex", "1");
        qp.append("pageSize", "100");
        if (tenantId && tenantId !== "estate") qp.append("tenantId", tenantId);
        const resTenant = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?${qp.toString()}`);
        if (resTenant.ok) {
          const jsonTenant = await resTenant.json();
          const tLogs = Array.isArray(jsonTenant?.data?.data) ? jsonTenant.data.data : (parseArrayData(jsonTenant) || []);
          if (tLogs.length > 0) logs = tLogs;
        }
      }
      if (!logs || logs.length === 0) {
        const resAll = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?pageIndex=1&pageSize=100`);
        if (resAll.ok) {
          const jsonAll = await resAll.json();
          logs = Array.isArray(jsonAll?.data?.data) ? jsonAll.data.data : (parseArrayData(jsonAll) || []);
        }
      }
      setAccessLogs((logs || []).map(normalizeAccessLog));
    } catch (err) {
      console.error(err);
      setAccessLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [estateId, tenantId, fullName, person.firstName]);

  useEffect(() => {
    fetchAccessCodes();
    fetchPersonAccessLogs();
  }, [fetchAccessCodes, fetchPersonAccessLogs]);

  const handleGenerateCode = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/dependents/${person.id}/access-codes`,
        { method: "POST", body: JSON.stringify({ isAccessCodePermanent: isPermanent, durationValue: Number(durationValue), durationUnit: Number(durationUnit) }) }
      );
      if (!res.ok) throw new Error("Failed to generate code.");
      toast.success("Access code generated!");
      fetchAccessCodes();
      onRefreshList?.();
    } catch (err) { toast.error(err.message); }
    finally { setGenerating(false); }
  };

  const handleDeactivateCode = async (codeId) => {
    if (!(await confirmDialog({ title: "Deactivate Access Code", message: "Deactivate this access code? It will stop working at the gate immediately.", confirmLabel: "Deactivate" }))) return;
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/dependents/${person.id}/access-codes/${codeId}/deactivate`,
        { method: "PATCH" }
      );
      if (!res.ok) throw new Error("Failed to deactivate code.");
      toast.success("Access code deactivated.");
      fetchAccessCodes();
      onRefreshList?.();
    } catch (err) { toast.error(err.message); }
  };

  const displayLogs = useMemo(() => {
    const rawList = accessLogs || [];
    const personCodes = [
      person.latestAccessCode,
      person.accessCode,
      ...(codes || []).map((c) => c.code || c.accessCode),
    ]
      .filter(Boolean)
      .map((c) => String(c).trim().toLowerCase());

    const fNameLower = (person.firstName || "").trim().toLowerCase();
    const lNameLower = (person.lastName || "").trim().toLowerCase();
    const fullLower = fullName.trim().toLowerCase();

    const personLogs = rawList.filter((l) => {
      if (person.id && (l.dependentId === person.id || l.subjectId === person.id || l.occupantId === person.id)) return true;
      const logCode = String(l.code || l.accessCode || "").trim().toLowerCase();
      if (logCode && personCodes.includes(logCode)) return true;
      const logName = String(l.subjectName || l.dependentName || l.visitorName || l.name || "").trim().toLowerCase();
      if (fullLower && logName.includes(fullLower)) return true;
      if (fNameLower && fNameLower.length >= 3 && logName.includes(fNameLower)) return true;
      if (lNameLower && lNameLower.length >= 3 && logName.includes(lNameLower)) return true;
      return false;
    });

    const targetLogs = personLogs.length > 0 ? personLogs : rawList;

    const events = [];
    targetLogs.forEach((l) => {
      const officerIn = officerLabel(l, "in").replace(/^by /, "");
      const officerOut = officerLabel(l, "out").replace(/^by /, "");
      const gateName = l.accessPoint || l.gate || "Main Gate";
      const codeVal = l.code || l.accessCode || person.latestAccessCode || person.accessCode;

      if (l.checkedInAt) {
        events.push({
          ...l,
          id: `${l.id}_in`,
          direction: "In",
          timestamp: l.checkedInAt,
          officer: officerIn,
          officerText: officerLabel(l, "in"),
          gate: gateName,
          code: codeVal,
          rawTime: new Date(l.checkedInAt).getTime(),
        });
      }
      if (l.checkedOutAt) {
        events.push({
          ...l,
          id: `${l.id}_out`,
          direction: "Out",
          timestamp: l.checkedOutAt,
          officer: officerOut,
          officerText: officerLabel(l, "out"),
          gate: gateName,
          code: codeVal,
          rawTime: new Date(l.checkedOutAt).getTime(),
        });
      }
      if (!l.checkedInAt && !l.checkedOutAt && (l.timestamp || l.createdAt)) {
        events.push({
          ...l,
          id: l.id,
          direction: directionOf(l.direction) || (l.type === "Exit" ? "Out" : "In"),
          timestamp: l.timestamp || l.createdAt,
          officer: l.verifiedBy || officerIn,
          officerText: l.verifiedBy ? `by ${l.verifiedBy}` : officerLabel(l, "in"),
          gate: gateName,
          code: codeVal,
          rawTime: new Date(l.timestamp || l.createdAt).getTime(),
        });
      }
    });

    events.sort((a, b) => (b.rawTime || 0) - (a.rawTime || 0));

    return events.filter((item) => {
      const dir = item.direction || "In";
      if (directionFilter === "in" && dir !== "In") return false;
      if (directionFilter === "out" && dir !== "Out") return false;

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchGate = (item.gate || "").toLowerCase().includes(q);
        const matchCode = (item.code || "").toLowerCase().includes(q);
        const matchOfficer = (item.officer || "").toLowerCase().includes(q);
        if (!matchGate && !matchCode && !matchOfficer) return false;
      }

      if (dateFilter !== "all") {
        const logTime = item.rawTime;
        const now = Date.now();
        let cutoff = 0;
        if (dateFilter === "today") {
          cutoff = new Date().setHours(0, 0, 0, 0);
        } else if (dateFilter === "7days" || dateFilter === "last7") {
          cutoff = now - 86400000 * 7;
        } else if (dateFilter === "30days" || dateFilter === "last30") {
          cutoff = now - 86400000 * 30;
        }
        if (cutoff && logTime < cutoff) return false;
      }

      return true;
    });
  }, [accessLogs, codes, person, fullName, directionFilter, searchQuery, dateFilter]);

  const activeVisit = useMemo(() => {
    const rawPersonLogs = (accessLogs || []).filter((l) => {
      if (person.id && (l.dependentId === person.id || l.subjectId === person.id || l.occupantId === person.id)) return true;
      const logCode = String(l.code || l.accessCode || "").trim().toLowerCase();
      const personCodes = [
        person.latestAccessCode,
        person.accessCode,
        ...(codes || []).map((c) => c.code || c.accessCode),
      ]
        .filter(Boolean)
        .map((c) => String(c).trim().toLowerCase());
      if (logCode && personCodes.includes(logCode)) return true;
      const fullLower = fullName.trim().toLowerCase();
      const fNameLower = (person.firstName || "").trim().toLowerCase();
      const logName = String(l.subjectName || l.dependentName || l.visitorName || l.name || "").trim().toLowerCase();
      if (fullLower && logName.includes(fullLower)) return true;
      if (fNameLower && fNameLower.length >= 3 && logName.includes(fNameLower)) return true;
      return false;
    });

    rawPersonLogs.sort((a, b) => {
      const tA = new Date(a.checkedInAt || a.timestamp || a.createdAt || 0).getTime();
      const tB = new Date(b.checkedInAt || b.timestamp || b.createdAt || 0).getTime();
      return tB - tA;
    });

    const latest = rawPersonLogs[0];
    if (latest) {
      const isInside = latest.isActiveVisit === true || (latest.checkedInAt && !latest.checkedOutAt) || directionOf(latest.direction) === "In" || latest.status === "Inside";
      if (isInside) {
        return {
          checkedInAt: latest.checkedInAt || latest.timestamp || latest.createdAt,
          checkedOutAt: latest.checkedOutAt,
          isActive: true,
          subjectType: latest.subjectType || type || "Resident",
        };
      }
    }

    if (displayLogs && displayLogs.length > 0) {
      const latestEvent = displayLogs[0];
      if (latestEvent && latestEvent.direction === "In") {
        return {
          checkedInAt: latestEvent.timestamp || latestEvent.checkedInAt,
          checkedOutAt: null,
          isActive: true,
          subjectType: latestEvent.subjectType || type || "Resident",
        };
      }
    }

    if (person.inEstate === true || person.isInEstate === true) {
      return {
        checkedInAt: person.lastCheckedInAt || person.checkedInAt || new Date().toISOString(),
        checkedOutAt: null,
        isActive: true,
        subjectType: type || "Resident",
      };
    }

    return null;
  }, [accessLogs, displayLogs, person, codes, fullName, type]);

  const imgSrc = person.imageUrl || person.photoUrl || person.avatarUrl || person.image;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-key"
      title="Access Pass Details"
      onClose={handleClose}
      width={760}
      fill
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>
          <button type="button" className="btn btn-danger d6-mbtn" onClick={() => { handleClose(); onDelete(person.id); }}>
            <i className="ki-outline ki-trash fs-5 me-2.5"></i>Remove Dependant
          </button>
        </>
      }
    >
      <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, padding: 24, overflowY: "auto" }}>
      <div className="d6m-person">
        <Avatar src={imgSrc} name={fullName} size={64} />
        <div className="min-w-0 flex-grow-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="d6m-name">{fullName}</span>
            <StatusChip color="#2563EB">{type || "Dependant"}</StatusChip>
          </div>
          <div className="d6m-muted mt-1"><i className="ki-outline ki-phone fs-7 me-1"></i>{person.phoneNumber || person.phone || "—"}</div>
        </div>
      </div>

      <div className="d6m-kv d6m-kv-3 mt-5">
        <div className="d6m-kv-cell"><span className="d6m-k">Building</span><span className="d6m-v">{person.buildingNumber || "—"}</span></div>
        <div className="d6m-kv-cell"><span className="d6m-k">Access code</span><span className="d6m-v">{person.latestAccessCode || person.accessCode || "—"}</span></div>
        <div className="d6m-kv-cell"><span className="d6m-k">Status</span><span className="d6m-v">Active</span></div>
      </div>

      {activeVisit && (
        <div className="mt-5">
          <VisitProgressBar
            checkIn={activeVisit.checkedInAt}
            checkOut={activeVisit.checkedOutAt}
            isActive={true}
            subjectType={activeVisit.subjectType || type || "Resident"}
            limitAt={(() => {
              const used = activeVisit.code || activeVisit.accessCode || person.latestAccessCode || person.accessCode;
              const pass = codes.find((c) => used && c.code === used) || codes.find((c) => c.isActive !== false);
              if (!pass || pass.isPermanent || !pass.expiresAt) return null;
              const t = new Date(pass.expiresAt).getTime();
              return Number.isNaN(t) ? null : t;
            })()}
          />
        </div>
      )}

      <div className="d6m-section d-flex flex-column flex-grow-1" style={{ minHeight: 260 }}>
        <div className="d6m-section-head">
          <span>Access log</span>
          <select className="d6m-input" style={{ width: "auto", height: 34, padding: "0 28px 0 10px" }} value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
        {loadingLogs ? (
          <div className="d-flex flex-column gap-2">
            {[1, 2, 3].map(n => <span key={n} className="d6m-shimmer" style={{ height: 52, borderRadius: 12 }} />)}
          </div>
        ) : displayLogs.length > 0 ? (
          <div className="d6m-list flex-grow-1" style={{ minHeight: 0, overflowY: "auto" }}>
            {displayLogs.map((log, idx) => {
              const isOut = log.direction === "Out" || log.type === "Exit";
              const when = log.timestamp || log.checkedInAt ? fmtDateTime(log.timestamp || log.checkedInAt) : "—";
              const officer = toTitleCase(log.officer || log.verifiedBy || log.checkedInByName || log.checkedOutByName || "Security Officer");
              return (
                <div key={idx} className="d6m-list-row" style={{ cursor: "default" }}>
                  <StatusChip color={isOut ? "#71717a" : "#16A34A"}>{isOut ? "OUT" : "IN"}</StatusChip>
                  <span className="min-w-0 flex-grow-1 text-start">
                    <span className="d6m-v d-block">{isOut ? "Checked out" : "Checked in"} · {when}</span>
                    <span className="d6m-muted d-block text-truncate">{log.officerText || `by ${officer}`}</span>
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="d6m-empty flex-grow-1 d-flex align-items-center justify-content-center">No access logs found for the selected filter.</div>
        )}
      </div>
      </div>
    </ModalShell>
  );
}



function EditTenantModal({ modalRef, tenant, estateId, adminId, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isCustomTitle, setIsCustomTitle] = useState(false);
  const [form, setForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    occupation: "",
    corporateName: ""
  });
  const [contactPerson, setContactPerson] = useState({
    id: "",
    title: "Mr",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    jobTitle: ""
  });
  const [deletingContactPerson, setDeletingContactPerson] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tenant) {
      const isCorporate = tenant.isCorporateTenant || tenant.isCorp;
      setForm({
        title: tenant.title || "",
        firstName: tenant.firstName || "",
        lastName: tenant.lastName || "",
        email: tenant.email || "",
        phoneNumber: tenant.phoneNumber || tenant.phone || "",
        occupation: tenant.occupation || "nil",
        corporateName: isCorporate ? (tenant.companyName || tenant.name || `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim()) : ""
      });
      setImagePreview(tenant.imageUrl || tenant.avatar || null);
      setImageFile(null);
      setErrors({});

      if (isCorporate) {
        const tenantId = tenant.tenantId || tenant.id;
        authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/contact-persons`)
          .then(r => r.ok ? r.json() : null)
          .then(json => {
            const data = parseArrayData(json);
            if (data && data.length > 0) {
              setContactPerson({
                id: data[0].id || "",
                title: data[0].title || "Mr",
                firstName: data[0].firstName || "",
                lastName: data[0].lastName || "",
                email: data[0].email || "",
                phoneNumber: data[0].phoneNumber || "",
                jobTitle: data[0].jobTitle || "",
              });
            } else {
              setContactPerson({ id: "", title: "Mr", firstName: "", lastName: "", email: "", phoneNumber: "", jobTitle: "" });
            }
          })
          .catch(() => {
            setContactPerson({ id: "", title: "Mr", firstName: "", lastName: "", email: "", phoneNumber: "", jobTitle: "" });
          });
      }
    }
  }, [tenant, estateId]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const isCorporate = tenant?.isCorporateTenant || tenant?.isCorp;
    const e = {};
    if (isCorporate) {
      if (!form.corporateName.trim()) e.corporateName = "Required";
      if (contactPerson.email.trim() && !/\S+@\S+\.\S+/.test(contactPerson.email)) {
        e.contactPersonEmail = "Valid email required";
      }
    } else {
      if (!form.firstName.trim()) e.firstName = "Required";
      if (!form.lastName.trim()) e.lastName = "Required";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
      if (!form.phoneNumber.trim()) e.phoneNumber = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const isCorporate = tenant?.isCorporateTenant || tenant?.isCorp;
    setSaving(true);
    try {
      let imageUrl = tenant?.imageUrl || tenant?.avatar || undefined;
      if (imageFile && adminId) {
        setUploadingImage(true);
        const uploaded = await uploadAdminImage(imageFile, adminId);
        setUploadingImage(false);
        if (uploaded) {
          imageUrl = uploaded;
        } else {
          toast("Photo upload failed — other changes will still be saved.", { icon: "⚠️" });
        }
      }

      const tenantId = tenant?.tenantId || tenant?.id;

      // Update tenant details
      let res;
      if (isCorporate) {
        const payload = {
          name: form.corporateName.trim(),
          email: contactPerson.email.trim(),
          phoneNumber: contactPerson.phoneNumber.trim(),
          sector: form.occupation.trim() || "nil",
          ...(imageUrl && { imageUrl }),
        };
        res = await authFetch(`${API_BASE}/estates/${estateId}/corporate-tenants/${tenantId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        const payload = {
          title: form.title,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: form.phoneNumber.trim(),
          occupation: form.occupation.trim() || "nil",
          ...(imageUrl && { imageUrl }),
        };
        res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) throw new Error("Failed to update tenant.");

      // Update contact person if corporate
      if (isCorporate && (contactPerson.firstName.trim() || contactPerson.lastName.trim() || contactPerson.id)) {
        const payload = {
          title: contactPerson.title || "Mr",
          firstName: contactPerson.firstName.trim(),
          lastName: contactPerson.lastName.trim(),
          email: contactPerson.email.trim(),
          phoneNumber: contactPerson.phoneNumber.trim(),
          jobTitle: contactPerson.jobTitle?.trim() || "",
        };
        const cpUrl = contactPerson.id
          ? `${API_BASE}/estates/${estateId}/tenants/${tenantId}/contact-persons/${contactPerson.id}`
          : `${API_BASE}/estates/${estateId}/tenants/${tenantId}/contact-persons`;
        const cpMethod = contactPerson.id ? "PUT" : "POST";

        const cpRes = await authFetch(cpUrl, {
          method: cpMethod,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (!cpRes.ok) {
          toast("Tenant updated, but saving Contact Person details failed.", { icon: "⚠️" });
        }
      }

      toast.success("Tenant updated successfully.");
      handleClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleDeleteContactPerson = async () => {
    if (!contactPerson.id) return;
    if (!(await confirmDialog({ title: "Delete Contact Person", message: "Are you sure you want to delete this contact person?", confirmLabel: "Delete" }))) return;
    setDeletingContactPerson(true);
    try {
      const tenantId = tenant?.tenantId || tenant?.id;
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/contact-persons/${contactPerson.id}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const text = await res.text();
        const json = safeJsonParse(text);
        throw new Error(json?.message || "Failed to delete contact person.");
      }
      toast.success("Contact person deleted successfully.");
      setContactPerson({ id: "", title: "Mr", firstName: "", lastName: "", email: "", phoneNumber: "", jobTitle: "" });
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || "Failed to delete contact person.");
    } finally {
      setDeletingContactPerson(false);
    }
  };

  const isCorporate = tenant?.isCorporateTenant || tenant?.isCorp;
  const fe = (k) => errors[k] ? <div className="fs-8 text-danger mt-1">{errors[k]}</div> : null;

  const titleIsKnown = TITLE_OPTIONS.includes(form.title);
  const showCustomTitle = isCustomTitle || (form.title && !titleIsKnown);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setCp = (k) => (e) => setContactPerson((p) => ({ ...p, [k]: e.target.value }));

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-pencil"
      title="Edit Tenant"
      onClose={handleClose}
      closeDisabled={saving}
      footer={
        <>
          {isCorporate && contactPerson.id && (
            <button type="button" className="btn btn-light-danger d6-mbtn me-auto" onClick={handleDeleteContactPerson} disabled={deletingContactPerson || saving}>
              {deletingContactPerson ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="ki-outline ki-trash fs-5 me-2"></i>}
              Delete Contact Person
            </button>
          )}
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={saving || uploadingImage}>
            {uploadingImage
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Uploading...</>
              : saving
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                : <><i className="ki-outline ki-check fs-5 me-2 text-white"></i>Save Changes</>}
          </button>
        </>
      }
    >
      <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">{isCorporate ? "Company logo" : "Tenant photo"}</div>
          <div className="d6m-muted">PNG or JPG, up to 5MB</div>
        </div>
        <div className="d-flex gap-2">
          {imagePreview && (
            <button type="button" className="btn btn-light-danger d6-mbtn" onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn" onClick={() => fileInputRef.current?.click()}>{imagePreview ? "Change" : "Upload"}</button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>

      {isCorporate ? (
        <div className="d-flex flex-column gap-4 mt-5">
          <div className="d6m-section-head mb-0"><span>Company information</span></div>
          <Field label="Company name" required error={errors.corporateName}>
            <input className="d6m-input" placeholder="Acme Corp" value={form.corporateName} onChange={set("corporateName")} />
          </Field>
          <div className="d6m-section-head mb-0 mt-2"><span>Primary contact person</span></div>
          <div className="row g-4">
            <Field className="col-4" label="Title">
              <select className="d6m-input" value={contactPerson.title || "Mr"} onChange={setCp("title")}>
                {["Mr", "Mrs", "Ms", "Dr", "Prof", "Chief"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field className="col-4" label="First name"><input className="d6m-input" placeholder="Jane" value={contactPerson.firstName} onChange={setCp("firstName")} /></Field>
            <Field className="col-4" label="Last name"><input className="d6m-input" placeholder="Doe" value={contactPerson.lastName} onChange={setCp("lastName")} /></Field>
          </div>
          <Field label="Contact email" error={errors.contactPersonEmail}>
            <input type="email" className="d6m-input" placeholder="jane@acmecorp.com" value={contactPerson.email} onChange={setCp("email")} />
          </Field>
          <Field label="Contact phone">
            <input type="tel" className="d6m-input" placeholder="e.g. 08012345678 or +2348012345678" value={contactPerson.phoneNumber} onChange={setCp("phoneNumber")} />
          </Field>
          <div className="row g-4">
            <Field className="col-6" label="Job title"><input className="d6m-input" placeholder="e.g. CEO" value={contactPerson.jobTitle} onChange={setCp("jobTitle")} /></Field>
            <Field className="col-6" label="Sector / notes"><input className="d6m-input" placeholder="e.g. Technology" value={form.occupation} onChange={set("occupation")} /></Field>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-4 mt-5">
          <div className="d6m-section-head mb-0"><span>Personal details</span></div>
          <Field label="Title">
            <select
              className="d6m-input"
              value={isCustomTitle ? "OTHERS" : (titleIsKnown ? form.title : (form.title ? "OTHERS" : ""))}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "OTHERS") setIsCustomTitle(true);
                else { setIsCustomTitle(false); setForm((p) => ({ ...p, title: val })); }
              }}
            >
              <option value="">None</option>
              {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="OTHERS">OTHERS</option>
            </select>
            {showCustomTitle && (
              <input type="text" className="d6m-input mt-2" placeholder="Type custom title..." value={titleIsKnown ? "" : form.title} onChange={set("title")} />
            )}
          </Field>
          <div className="row g-4">
            <Field className="col-6" label="First name" required error={errors.firstName}><input className="d6m-input" placeholder="John" value={form.firstName} onChange={set("firstName")} /></Field>
            <Field className="col-6" label="Last name" required error={errors.lastName}><input className="d6m-input" placeholder="Doe" value={form.lastName} onChange={set("lastName")} /></Field>
          </div>
          <Field label="Email address" required error={errors.email}>
            <input type="email" className="d6m-input" placeholder="johndoe@zangon.com" value={form.email} onChange={set("email")} />
          </Field>
          <Field label="Phone number" required error={errors.phoneNumber}>
            <input type="tel" className="d6m-input" placeholder="e.g. 08012345678 or +2348012345678" value={form.phoneNumber} onChange={set("phoneNumber")} />
          </Field>
          <Field label="Occupation">
            <input className="d6m-input" placeholder="e.g. Software Engineer" value={form.occupation} onChange={set("occupation")} />
          </Field>
        </div>
      )}
    </ModalShell>
  );
}

function PrimaryOccupantModal({ modalRef, tenant, estateId, buildings, primaryOccupants = [], onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phoneNumber: "", buildingId: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const availableBuildings = useMemo(() => {
    if (!buildings || buildings.length === 0) return [];
    if (!primaryOccupants || primaryOccupants.length === 0) return buildings;

    return buildings.filter(b => {
      const bId = b.id || b.buildingId;
      const isAssigned = primaryOccupants.some(occ => {
        const occBId = occ.currentBuildingId || occ.buildingId || occ.assignedBuildingId || occ.building?.id;
        if (occBId && bId && String(occBId) === String(bId)) return true;
        if (occ.currentBuildingNumber && b.number && String(occ.currentBuildingNumber).trim() === String(b.number).trim()) {
          const phase1 = String(occ.currentPhaseName || "").trim();
          const phase2 = String(b.phaseName || "").trim();
          if (!phase1 || !phase2 || phase1 === phase2) return true;
        }
        return false;
      });
      return !isAssigned;
    });
  }, [buildings, primaryOccupants]);

  useEffect(() => {
    if (availableBuildings && availableBuildings.length > 0) {
      setForm(prev => ({ ...prev, buildingId: availableBuildings[0].id || availableBuildings[0].buildingId || "" }));
    } else {
      setForm(prev => ({ ...prev, buildingId: "" }));
    }
  }, [availableBuildings]);

  useEffect(() => {
    const fName = form.firstName.trim().toLowerCase().replace(/\s+/g, '');
    const lName = form.lastName.trim().toLowerCase().replace(/\s+/g, '');
    if (fName || lName) {
      setForm(prev => ({ ...prev, email: `${fName}${lName}@zangon.com` }));
    } else {
      setForm(prev => ({ ...prev, email: "" }));
    }
  }, [form.firstName, form.lastName]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.buildingId) { toast.error("First Name, Last Name, and Building are required."); return; }
    setSaving(true);
    try {
      let imageUrl = undefined;
      if (imageFile) {
        const uploaded = await uploadAdminImage(imageFile, getSession()?.userId);
        if (uploaded) imageUrl = uploaded;
      }
      const tenantId = tenant?.tenantId || tenant?.id;
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants`, {
        method: "POST",
        body: JSON.stringify({ firstName: form.firstName, lastName: form.lastName, email: form.email, phoneNumber: form.phoneNumber, ...(imageUrl && { imageUrl }) }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to create Primary Occupant.");
      const newOccupantId = typeof json?.data === "object" ? (json?.data?.id || json?.data?.primaryOccupantId) : json?.data;
      if (newOccupantId && form.buildingId) {
        const assignRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/primary-occupants/${newOccupantId}/buildings/${form.buildingId}/assign`, { method: "POST", body: JSON.stringify({}) });
        if (!assignRes.ok) {
          toast.error("Occupant created, but failed to link to the building.");
        } else {
          toast.success("Primary Occupant added and assigned!");
        }
      } else {
        toast.success("Primary Occupant added!");
      }
      if (window.bootstrap?.Modal && modalRef.current) window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
      onSaved?.();
      setForm({ firstName: "", lastName: "", email: "", phoneNumber: "", buildingId: availableBuildings[0]?.id || availableBuildings[0]?.buildingId || "" });
      setImagePreview(null); setImageFile(null);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const closePO = () => { if (window.bootstrap?.Modal && modalRef.current) window.bootstrap.Modal.getInstance(modalRef.current)?.hide(); };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-user-tick"
      title="Add Primary Occupant"
      onClose={closePO}
      closeDisabled={saving}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={closePO} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="ki-outline ki-check fs-5 me-2 text-white"></i>Save</>}
          </button>
        </>
      }
    >
      <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">Occupant photo</div>
          <div className="d6m-muted">PNG or JPG, up to 5MB</div>
        </div>
        <button type="button" className="btn btn-light d6-mbtn" onClick={() => fileInputRef.current?.click()}>{imagePreview ? "Change" : "Upload"}</button>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>

      <div className="d-flex flex-column gap-4 mt-5">
        <Field label="Assign to building" required>
          <select className="d6m-input" value={form.buildingId} onChange={e => setForm({ ...form, buildingId: e.target.value })}>
            {availableBuildings.length === 0 ? (
              <option value="">No unassigned buildings available</option>
            ) : (
              availableBuildings.map(b => <option key={b.id || b.buildingId} value={b.id || b.buildingId}>{b.phaseName ? `${b.phaseName} - ` : ""}{b.number}</option>)
            )}
          </select>
        </Field>
        <div className="row g-4">
          <Field className="col-6" label="First name" required><input className="d6m-input" placeholder="First Name" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} /></Field>
          <Field className="col-6" label="Last name" required><input className="d6m-input" placeholder="Last Name" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} /></Field>
        </div>
        <Field label="Email">
          <input type="email" className="d6m-input" placeholder="firstlast@zangon.com" value={form.email} readOnly disabled />
        </Field>
        <Field label="Phone">
          <input type="tel" className="d6m-input" placeholder="e.g. 08012345678 or +2348012345678" value={form.phoneNumber || ""} onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value }))} />
        </Field>
      </div>
    </ModalShell>
  );
}

function UnassignSingleBuildingModal({ modalRef, building, estateId, tenantId, onUnassigned }) {
  const [unassigning, setUnassigning] = useState(false);

  if (!building) return null;

  const houseImg = building.imageUrl || getHouseImage(building.typeName || building.buildingTypeName || building.name);
  const phaseName = building.phaseName || building.phase || "Phase 4";
  const typeName = building.typeName || building.buildingTypeName || "Property";
  const bNum = building.number || building.buildingNumber || building.houseNumber || "";
  const bNumStr = String(bNum).trim();
  const bId = building.id || building.buildingId;

  const handleConfirmUnassign = async () => {
    if (!bId) return;
    setUnassigning(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/buildings/${bId}/unassign`, {
        method: "PATCH",
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error("Failed to unassign building.");
      toast.success("Building unassigned successfully.");
      if (window.bootstrap?.Modal && modalRef.current) {
        window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
      }
      if (onUnassigned) onUnassigned();
    } catch (err) {
      toast.error(err.message || "Failed to unassign building.");
    } finally {
      setUnassigning(false);
    }
  };

  const closeUS = () => { if (window.bootstrap?.Modal && modalRef.current) window.bootstrap.Modal.getInstance(modalRef.current)?.hide(); };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-home-2"
      title="Unassign Building"
      onClose={closeUS}
      closeDisabled={unassigning}
      width={560}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" disabled={unassigning} onClick={closeUS}>Cancel</button>
          <button type="button" className="btn btn-danger d6-mbtn" disabled={unassigning} onClick={handleConfirmUnassign}>
            {unassigning ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Unassigning...</> : <><i className="ki-outline ki-trash fs-5 me-2"></i>Unassign Building</>}
          </button>
        </>
      }
    >
      <div className="position-relative overflow-hidden mb-4" style={{ height: 180, borderRadius: 14 }}>
        <img src={houseImg} alt={typeName} className="w-100 h-100 object-fit-cover" onError={(e) => { e.target.src = "/assets/media/zangon/3bedroomsbungalo.jpg"; }} />
        <div className="position-absolute bottom-0 start-0 end-0" style={{ height: 80, background: "linear-gradient(to top, rgba(0,0,0,.55), transparent)" }} />
        <div className="position-absolute d-flex gap-2" style={{ left: 12, bottom: 12 }}>
          <span className="badge bg-white text-dark fw-bold">{bNumStr ? `Building ${bNumStr}` : typeName}</span>
          <span className="badge bg-success text-white fw-bold">{phaseName}</span>
        </div>
      </div>
      <div className="d6m-note">
        <i className="ki-outline ki-information-5 fs-3"></i>
        <div>
          <div className="fw-bold text-gray-900 mb-1">Remove this building assignment?</div>
          This will unassign the {typeName.toLowerCase()} from this tenant profile.
        </div>
      </div>
    </ModalShell>
  );
}

const PAGE_SIZE = 10;

const CAR_MAKES = ["Toyota","Lexus","Mercedes-Benz","Honda","Hyundai","Acura","Audi","BMW","BYD","Cadillac","Changan","Chery","Chevrolet","Chrysler","Citroen","Daihatsu","DFSK","Dodge","Dongfeng","FAW","Ferrari","Fiat","Ford","GAC","Geely","Genesis","GMC","Great Wall","Haima","Haval","Hino","Hongqi","Hummer","Infiniti","Isuzu","Iveco","IVM","JAC","Jaguar","Jeep","Jetour","Jinbei","JMC","Kantanka","Kia","King Long","Lamborghini","Land Rover","Lincoln","Lucid","Maserati","Maxus","Mazda","MG","Mini","Mitsubishi","NIO","Nissan","Nord","Opel","Peugeot","Polestar","Pontiac","Porsche","RAM","Renault","Rivian","Rolls-Royce","Rover","Scion","Skoda","Subaru","Suzuki","Tesla","Volkswagen","Volvo","XPeng","Zeekr","Other Make"];
const CAR_COLORS = ["Black","White","Silver","Gray","Blue","Red","Green","Gold","Brown"];
const CAR_YEARS = Array.from({ length: 38 }, (_, i) => 2027 - i);
const CAR_MODELS = {
  Toyota: ["Avalon","Camry","Corolla","Fortuner","Hiace","Highlander","Hilux","Land Cruiser","Prado","RAV4","Sienna","Venza","Yaris","Sequoia","Tacoma","4Runner","bZ4X"],
  Lexus: ["ES 250","ES 300","ES 350","GX 460","GX 470","IS 250","IS 300","IS 350","LX 570","LX 600","NX 300","NX 350","RX 330","RX 350","RX 500h","RZ 450e"],
  "Mercedes-Benz": ["A-Class","B-Class","C-Class","CLA","CLS","E-Class","G-Class","GLA","GLK","GLB","GLC","GLE","GLS","S-Class","Maybach","Sprinter","EQA","EQB","EQE","EQS"],
  Honda: ["Accord","Civic","CR-V","HR-V","Pilot","Ridgeline","Odyssey","Fit","City","Passport","Insight","Crosstour","Prologue","e:Ny1"],
  Hyundai: ["Accent","Elantra","Sonata","Tucson","Santa Fe","Palisade","Creta","Venue","Kona","Veloster","Grand i10","Azera","Ioniq 5","Ioniq 6","Kona Electric"],
  Ford: ["Edge","Escape","Explorer","Everest","F-150","Ranger","Transit","Mustang","Focus","Fusion","Bronco","Expedition","Mustang Mach-E","F-150 Lightning"],
  Chevrolet: ["Camaro","Corvette","Cruze","Malibu","Impala","Equinox","Tahoe","Suburban","Silverado","Traverse","Trailblazer","Bolt EV","Equinox EV","Blazer EV"],
  Nissan: ["Altima","Maxima","Sentra","Sunny","Micra","Versa","Rogue","X-Trail","Pathfinder","Patrol","Armada","Frontier","Leaf","Ariya"],
  Kia: ["Rio","Picanto","Cerato","Optima","K5","Sportage","Sorento","Telluride","Seltos","Carnival","Stinger","EV6","EV9","Niro EV"],
  Jeep: ["Wrangler","Cherokee","Grand Cherokee","Compass","Renegade","Gladiator","Commander"],
  "Land Rover": ["Defender","Discovery","Discovery Sport","Range Rover","Range Rover Sport","Range Rover Velar","Range Rover Evoque"],
  Porsche: ["911","Cayenne","Macan","Panamera","Taycan","Boxster","Macan Electric"],
  BMW: ["1 Series","2 Series","3 Series","4 Series","5 Series","7 Series","X1","X3","X5","X6","X7","M3","M5","i4","i5","i7","iX","iX3"],
  Audi: ["A3","A4","A5","A6","A7","A8","Q3","Q5","Q7","Q8","RS5","RS7","TT","e-tron","e-tron GT","Q4 e-tron","Q8 e-tron"],
  Volkswagen: ["Golf","Jetta","Passat","Polo","Tiguan","Touareg","Atlas","ID.4","ID.3","ID. Buzz"],
  Tesla: ["Model S","Model 3","Model X","Model Y","Cybertruck"],
  Maxus: ["T60", "T60 Max", "T70", "T90 EV", "eTerron 9", "D60", "D90", "G10", "Mifa 9"],
  Volvo: ["EX30", "EX90", "C40 Recharge"],
  Jaguar: ["XE", "XF", "F-Pace", "E-Pace", "I-Pace", "F-Type"],
  BYD: ["Atto 3", "Dolphin", "Seal", "Seagull", "Han", "Tang", "Song Plus", "Sealion 7"],
  MG: ["MG3", "MG5", "MG ZS", "MG ZS EV", "MG4 EV", "MG HS", "Cyberster"],
  Rivian: ["R1T", "R1S"],
  Lucid: ["Air", "Gravity"],
  Polestar: ["Polestar 2", "Polestar 3", "Polestar 4"],
  NIO: ["ET5", "ET7", "ES6", "ES8"],
  Zeekr: ["001", "007", "X"],
  XPeng: ["P7", "G6", "G9"],
  Acura: ["MDX", "RDX", "TLX", "ILX", "Integra"],
  Cadillac: ["Escalade", "CT4", "CT5", "XT4", "XT5", "XT6", "Lyriq"],
  Changan: ["CS35 Plus", "CS55 Plus", "CS75 Plus", "CS85", "CS95", "UNI-K", "UNI-V"],
  Chery: ["Tiggo 2", "Tiggo 4 Pro", "Tiggo 7 Pro", "Tiggo 8 Pro", "Arrizo 8"],
  Chrysler: ["300", "Pacifica", "Voyager", "Town & Country"],
  Citroen: ["C3", "C4", "C5 Aircross", "Berlingo"],
  Daihatsu: ["Terios", "Sirion", "Mira", "Move", "Tanto"],
  Dodge: ["Charger", "Challenger", "Durango", "Hornet", "Journey"],
  Dongfeng: ["Nammi 01", "Aeolus Yixuan", "M-Hero 917", "Rich 6", "Forthing T5 EVO"],
  Ferrari: ["296 GTB", "SF90 Stradale", "F8 Tributo", "Roma", "Purosangue", "812 Superfast"],
  Fiat: ["500", "500X", "Panda", "Tipo", "Punto"],
  GAC: ["GS3", "GS4", "GS8", "GN8", "Empow"],
  Geely: ["Coolray", "Azkarra", "Monjaro", "Emgrand", "Tugella", "Galaxy L7", "Galaxy E8"],
  Genesis: ["G70", "G80", "G90", "GV70", "GV80", "GV60"],
  GMC: ["Sierra 1500", "Yukon", "Acadia", "Terrain", "Canyon", "Hummer EV"],
  "Great Wall": ["Poer", "Wingle 5", "Wingle 7", "Cannon"],
  Haval: ["H6", "Jolion", "Dargo", "H9"],
  Hongqi: ["E-HS9", "EH7", "EHS7", "H5", "H9", "HQ9", "HS5"],
  Hummer: ["H1", "H2", "H3", "H3T"],
  Infiniti: ["Q50", "Q60", "QX50", "QX55", "QX60", "QX80"],
  Isuzu: ["D-Max", "MU-X", "Trooper"],
  IVM: ["G5", "G40", "G80", "Fox", "Umu", "Connect", "Carrier"],
  JAC: ["JS4", "JS6", "JS8", "T6", "T8 Pro", "E30X", "e-J7"],
  Jetour: ["Dashing", "X70", "X90", "Traveller T2", "Shanhai T2", "Shanhai L9"],
  Lamborghini: ["Hurac\u00e1n", "Aventador", "Urus", "Revuelto", "Temerario"],
  Lincoln: ["Navigator", "Aviator", "Nautilus", "Corsair", "Continental"],
  Maserati: ["Ghibli", "Quattroporte", "Levante", "Grecale", "MC20"],
  Mazda: ["Mazda3", "Mazda6", "CX-30", "CX-5", "CX-50", "CX-90", "MX-5 Miata"],
  Mini: ["Cooper", "Clubman", "Countryman", "Aceman"],
  Mitsubishi: ["Lancer", "Outlander", "Pajero", "ASX", "Eclipse Cross", "L200"],
  Opel: ["Corsa", "Astra", "Mokka", "Crossland", "Grandland", "Insignia"],
  Peugeot: ["206", "207", "301", "307", "308", "5008"],
  Pontiac: ["G6", "G8", "Grand Prix", "Firebird", "Vibe", "Solstice"],
  RAM: ["1500", "2500", "3500", "ProMaster", "Rampage"],
  Renault: ["Clio", "Megane", "Captur", "Kadjar", "Koleos", "Duster", "5 E-Tech"],
  "Rolls-Royce": ["Phantom", "Ghost", "Cullinan", "Wraith", "Dawn", "Spectre"],
  Rover: ["Mini", "25", "45", "75", "Streetwise", "SD1"],
  Scion: ["tC", "xB", "xD", "FR-S", "iA", "iM"],
  Skoda: ["Fabia", "Octavia", "Superb", "Kamiq", "Karoq", "Kodiaq", "Enyaq"],
  Subaru: ["Impreza", "Legacy", "Crosstrek", "Forester", "Outback", "WRX", "BRZ", "Solterra"],
  Suzuki: ["Swift", "Baleno", "Vitara", "Grand Vitara", "Jimny", "Ciaz", "Ertiga"],
  Nord: ["A3", "A5", "Tank", "Tusk", "Max"],
  Kantanka: ["Omama", "Onantefo", "Amoanimaa", "Opoku", "Mensah"],
  JMC: ["Vigus", "Yuhu", "Touring", "Carrying", "Boarding"],
  FAW: ["Bestune T77", "Bestune B70", "Bestune T99", "Sirius S80", "Jiefang J6"],
  Jinbei: ["Haise", "Grandview", "Granse", "F50"],
  DFSK: ["Glory 500", "Glory 580", "Glory iX5", "C31", "K01"],
  Haima: ["7X", "8S", "M3", "S5"],
  "King Long": ["Kingo", "Kaige", "Joylong", "XMQ6118"],
  Hino: ["Dutro", "300 Series", "500 Series", "700 Series", "Ranger"],
  Iveco: ["Daily", "Eurocargo", "S-Way", "Massif"]
};

function getCarImageUrl(make, model, year, color) {
  if (!make || !model) return null;
  return `https://cdn.imagin.studio/getimage?customer=img&make=${encodeURIComponent(make)}&modelFamily=${encodeURIComponent(model)}&modelYear=${year || 2020}&paintId=${encodeURIComponent(color || "Silver")}&angle=23&zoomType=fullscreen`;
}

// Activity tab labels
const ACTIVITY_TABS = ["Rent Invoices", "Payment History", "Maintenance Requests"];

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";
  const adminId = session?.userId || "";

  const [pageReady, setPageReady] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tab 0: rent invoices, Tab 1: payment ledger history, Tab 2: maintenance
  const [rentInvoices, setRentInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [primaryOccupants, setPrimaryOccupants] = useState([]);
  const [contactPersons, setContactPersons] = useState([]);


  const [cars, setCars] = useState([]);

  // Selected invoice for the InvoiceDetailModal (start payment flow)
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [addDriverCar, setAddDriverCar] = useState(null);
  const [viewDriversCar, setViewDriversCar] = useState(null);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const requestDetailModalRef = useRef(null);
  const requestDetailModal = useBootstrapModal(requestDetailModalRef);

  const handleOpenRequest = (req) => { setSelectedRequest(req); requestDetailModal.show(); };
  const handleStatusChange = (requestId, newStatus) => {
    setMaintenanceRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: newStatus } : r));
  };

  const editTenantModalRef = useRef(null);
  const editTenantModal = useBootstrapModal(editTenantModalRef);

  const primaryOccupantModalRef = useRef(null);
  const primaryOccupantModal = useBootstrapModal(primaryOccupantModalRef);

  const assignBuildingModalRef = useRef(null);
  const unassignAllModalRef = useRef(null);
  const addDependantModalRef = useRef(null);
  const addCarModalRef = useRef(null);
  const downloadCarLogModalRef = useRef(null);
  const unassignSingleBuildingModalRef = useRef(null);

  const [buildingToUnassign, setBuildingToUnassign] = useState(null);
  const [assignableBuildings, setAssignableBuildings] = useState([]);
  const [selectedBuildingIds, setSelectedBuildingIds] = useState([]);
  const [buildingSearchQuery, setBuildingSearchQuery] = useState("");
  const [assigningBuilding, setAssigningBuilding] = useState(false);
  const [unassigningAll, setUnassigningAll] = useState(false);
  const [selectedUnassignBuildingId, setSelectedUnassignBuildingId] = useState("");

  const [dependantForm, setDependantForm] = useState({
    firstName: "", lastName: "", phoneNumber: "", email: "",
    dependentType: 0, notes: "",
    generateAccessCode: true, isAccessCodePermanent: true,
    durationValue: 1, durationUnit: 0,
  });
  const [savingDependant, setSavingDependant] = useState(false);
  const [dependantImageFile, setDependantImageFile] = useState(null);
  const [dependantImagePreview, setDependantImagePreview] = useState(null);
  const dependantFileInputRef = useRef(null);

  const handleDeleteDependant = async (dependentId) => {
    if (!(await confirmDialog({ title: "Delete Dependant", message: "Are you sure you want to permanently delete this dependant? This cannot be undone.", confirmLabel: "Delete" }))) return;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/dependents/${dependentId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete dependant.");
      toast.success("Dependant deleted successfully.");
      setSelectedAccess(null);
      fetchDetails();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteCar = async (carId) => {
    if (!(await confirmDialog({ title: "Remove Car", message: "Are you sure you want to permanently delete this vehicle? This cannot be undone.", confirmLabel: "Remove Car" }))) return false;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/cars/${carId}`, { method: "DELETE" });
      if (!res.ok) {
        const text = await res.text();
        const json = safeJsonParse(text);
        throw new Error(json?.message || "Failed to delete vehicle.");
      }
      toast.success("Vehicle deleted successfully.");
      fetchDetails();
      return true;
    } catch (err) { toast.error(err.message); return false; }
  };

  const handleDeleteContactPerson = async (contactPersonId) => {
    if (!(await confirmDialog({ title: "Remove Contact Person", message: "Are you sure you want to remove this contact person?", confirmLabel: "Remove" }))) return;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/contact-persons/${contactPersonId}`, { method: "DELETE" });
      if (!res.ok) {
        const text = await res.text();
        const json = safeJsonParse(text);
        throw new Error(json?.message || "Failed to remove contact person.");
      }
      toast.success("Contact person removed successfully.");
      fetchDetails();
    } catch (err) { toast.error(err.message); }
  };

  const handleAddDependant = async () => {
    if (!dependantForm.firstName || !dependantForm.lastName) return toast.error("First and Last name are required.");
    setSavingDependant(true);
    try {
      let imageUrl = undefined;
      if (dependantImageFile) {
        const uploaded = await uploadAdminImage(dependantImageFile, adminId);
        if (uploaded) imageUrl = uploaded;
      }
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/dependents`, {
        method: "POST",
        body: JSON.stringify({
          firstName: dependantForm.firstName, lastName: dependantForm.lastName,
          phoneNumber: dependantForm.phoneNumber, email: dependantForm.email,
          dependentType: dependantForm.dependentType, notes: dependantForm.notes || "string",
          isAccessCodePermanent: dependantForm.generateAccessCode ? dependantForm.isAccessCodePermanent : true,
          durationValue: Number(dependantForm.durationValue),
          durationUnit: Number(dependantForm.durationUnit),
          ...(imageUrl && { imageUrl }),
        }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to add dependant.");
      const newDependentId = json?.data;

      if (newDependentId && dependantForm.generateAccessCode) {
        try {
          const accessCodeRes = await authFetch(
            `${API_BASE}/estates/${estateId}/tenants/${id}/dependents/${newDependentId}/access-codes`,
            { method: "POST", body: JSON.stringify({ isAccessCodePermanent: dependantForm.isAccessCodePermanent, durationValue: Number(dependantForm.durationValue), durationUnit: Number(dependantForm.durationUnit) }) }
          );
          if (!accessCodeRes.ok) { toast.error("Dependant created, but access code generation failed."); }
          else { toast.success("Dependant added and access code generated!"); }
        } catch { toast.error("Dependant added, but access code failed to generate."); }
      } else {
        toast.success("Dependant added successfully.");
      }

      setDependantForm({ firstName: "", lastName: "", phoneNumber: "", email: "", dependentType: 0, notes: "", generateAccessCode: true, isAccessCodePermanent: true, durationValue: 1, durationUnit: 0 });
      setDependantImageFile(null); setDependantImagePreview(null);
      fetchDetails();
      if (window.bootstrap?.Modal && addDependantModalRef.current) window.bootstrap.Modal.getInstance(addDependantModalRef.current)?.hide();
    } catch (err) { toast.error(err.message); }
    finally { setSavingDependant(false); }
  };

  const handleDependantImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setDependantImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setDependantImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const [carForm, setCarForm] = useState({ make: "", model: "", year: "", color: "", licensePlate: "" });
  const [carDrivers, setCarDrivers] = useState([]);
  const [savingCar, setSavingCar] = useState(false);
  const [terminateConfirm, setTerminateConfirm] = useState(false);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");
    const init = () => { window.KTThemeMode?.init?.(); window.KTComponents?.init?.(); };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  const fetchDetails = useCallback(async () => {
    if (!estateId || !id) { setPageReady(true); return; }
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/details`);
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to fetch tenant details");
      const data = json?.data || json;
      setTenant(data);

      try {
        const occRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/primary-occupants?pageIndex=1&pageSize=100`);
        const occJson = occRes.ok ? safeJsonParse(await occRes.text()) : null;
        setPrimaryOccupants(parseArrayData(occJson));
      } catch { }

      try {
        const carsRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/cars`);
        const carsJson = carsRes.ok ? safeJsonParse(await carsRes.text()) : null;
        setCars(parseArrayData(carsJson));
      } catch { }

      try {
        const bldgRes = await authFetch(`${API_BASE}/estates/${estateId}/buildings`);
        const bldgJson = bldgRes.ok ? safeJsonParse(await bldgRes.text()) : null;
        setAssignableBuildings(parseArrayData(bldgJson));
      } catch { }

      try {
        const depRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/dependents`);
        const depJson = depRes.ok ? safeJsonParse(await depRes.text()) : null;
        setAccessList(parseArrayData(depJson).map(normalizeDependent));
      } catch { }

      try {
        const cpRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/contact-persons`);
        const cpJson = cpRes.ok ? safeJsonParse(await cpRes.text()) : null;
        setContactPersons(parseArrayData(cpJson));
      } catch { }

    } catch (error) {
      toast.error(error.message);
      navigate("/tenants");
    } finally {
      setLoading(false);
      setPageReady(true);
    }
  }, [estateId, id, navigate]);

  const fetchedState = useRef({ estateId: null, id: null });
  useEffect(() => {
    const s = fetchedState.current;
    if (s.estateId === estateId && s.id === id) return;
    s.estateId = estateId; s.id = id;
    fetchDetails();
  }, [fetchDetails, estateId, id]);

  useEffect(() => {
    const interval = setInterval(() => {
      const session = getSession();
      if (!session?.accessTokenExpiresAt) return;
      const expiresAt = new Date(session.accessTokenExpiresAt).getTime();
      const timeUntilExpiry = expiresAt - Date.now();
      if (timeUntilExpiry > 0 && timeUntilExpiry < 3 * 60 * 1000) {
        refreshAccessToken().catch(err => console.warn("Background refresh failed.", err));
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchActivityTab = useCallback(async (tabIdx) => {
    if (!id || !estateId) return;
    try {
      if (tabIdx === 0) {
        // Tab 0: Rent Invoices — GET tenant rent invoices
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/rent-invoices`);
        const json = res.ok ? safeJsonParse(await res.text()) : null;
        let mapped = json?.data?.data && Array.isArray(json.data.data) ? json.data.data : parseArrayData(json);
        mapped = mapped.sort((a, b) => new Date(b.issuedAt || b.createdDate || 0) - new Date(a.issuedAt || a.createdDate || 0));
        setRentInvoices(mapped);
      } else if (tabIdx === 1) {
        // Tab 1: Payment Ledger History — actual PayU transactions
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/payments`);
        const json = res.ok ? safeJsonParse(await res.text()) : null;
        let mapped = json?.data?.data && Array.isArray(json.data.data) ? json.data.data : parseArrayData(json);
        mapped = mapped.sort((a, b) => new Date(b.paidAt || b.createdDate || 0) - new Date(a.paidAt || a.createdDate || 0));
        setPayments(mapped);
      } else if (tabIdx === 2) {
        // Tab 2: Maintenance Requests
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/maintenance-requests?pageIndex=1&pageSize=100`);
        const json = res.ok ? safeJsonParse(await res.text()) : null;
        let mapped = json?.data?.data && Array.isArray(json.data.data) ? json.data.data : parseArrayData(json);
        mapped = mapped.map(normalizeMaintenanceRequest).sort((a, b) => new Date(b.createdAt || b.createdDate || 0) - new Date(a.createdAt || a.createdDate || 0));
        setMaintenanceRequests(mapped);
      }
    } catch (err) {
      console.error("fetchActivityTab error:", err);
    }
  }, [id, estateId]);

  useEffect(() => {
    if (pageReady && !loading) {
      fetchActivityTab(0);
      fetchActivityTab(1);
      fetchActivityTab(2);
    }
  }, [pageReady, loading, fetchActivityTab]);



  const { activeAccessList, inactiveAccessList, totalAccessCount } = useMemo(() => {
    const list = accessList;
    const active = [];
    const inactive = [];

    list.forEach(a => {
      if (a.isLatestAccessCodeActive === false || a.isActive === false || a.status === "Inactive") {
        inactive.push(a);
      } else {
        active.push(a);
      }
    });

    return {
      activeAccessList: active,
      inactiveAccessList: inactive,
      totalAccessCount: list.length
    };
  }, [accessList]);

  const renderAccessRowItem = useCallback((a, i, isInactive = false) => {
    const titleStr = a.title ? `${a.title} ` : "";
    const name = toTitleCase(`${titleStr}${a.firstName || ""} ${a.lastName || ""}`.trim()) || toTitleCase(a.name) || "Dependant";
    const initials = getInitials(name);

    // Staff and Family Member are permanent access
    const isPermanentAccess = 
      a.dependentType === "Staff" || 
      a.dependentType === "FamilyMember" || 
      a.dependentType === "Family Member" || 
      a.dependentType === 0 || 
      a.dependentType === 1 || 
      a.isPermanent === true;

    let roleTag = "Resident";
    if (typeof a.dependentType === "string") {
      if (a.dependentType === "FamilyMember") roleTag = "Family Member";
      else roleTag = a.dependentType;
    } else if (a.dependentType !== undefined) {
      roleTag = formatDependentType(a.dependentType) || (isPermanentAccess ? "Resident" : "Temporary");
    }

    const phone = a.phoneNumber || a.phone || a.email || (a.buildingNumber ? `Building ${a.buildingNumber}` : "—");
    const dateCreatedStr = a.createdAt || a.issuedAt ? fmtDate(a.createdAt || a.issuedAt) : "19 Aug 2026";
    const accessType = isPermanentAccess ? "Permanent" : "Temporary";

    return (
      <div 
        key={a.id || i} 
        className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all border-hover-success position-relative overflow-hidden ${isInactive ? "opacity-75" : ""}`} 
        style={{ minHeight: 84 }}
        onClick={() => setSelectedAccess(a)}
      >

        <div className="d-flex align-items-center me-4" style={{ width: "360px", flexShrink: 0 }}>
          <div className="symbol symbol-45px symbol-circle me-4 flex-shrink-0">
            {a.imageUrl ? (
              <img src={a.imageUrl} alt={name} className="object-fit-cover rounded-circle w-45px h-45px" onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <span className={`symbol-label rounded-circle w-45px h-45px fw-bold fs-5 ${isInactive ?"bg-light text-muted" : "bg-light-success text-success"}`}>{initials}</span>
            )}
          </div>
          <div className="d-flex flex-column overflow-hidden">
            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
              <span className="fs-6 fw-bold text-gray-900 text-truncate">{name}</span>
              <span className={`badge fs-8 fw-semibold ${isInactive ?"badge-light text-gray-600" : isPermanentAccess ? "badge-light-success text-success" : "badge-light-primary text-primary"}`}>{roleTag}</span>
            </div>
            <div className="text-gray-500 fs-7 text-truncate" title={phone}>{phone}</div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-6 text-end ms-auto flex-shrink-0">
          <div className="d-none d-sm-block text-center ms-4">
            <div className="text-gray-500 fs-8 mb-1">Date Created</div>
            <div className="fw-semibold text-gray-900 fs-7">{dateCreatedStr}</div>
          </div>
          <div className="text-center ms-4">
            <div className="text-gray-500 fs-8 mb-1">Access Type</div>
            <div className={`fw-bold fs-7 ${isInactive ?"text-gray-500" : isPermanentAccess ? "text-success" : "text-warning"}`}>
              {accessType}
            </div>
          </div>
        </div>
      </div>
    );
  }, []);

  const displayInvoices = useMemo(() => {
    return rentInvoices;
  }, [rentInvoices]);

  const displayPayments = useMemo(() => {
    return payments;
  }, [payments]);

  const displayCars = useMemo(() => {
    return cars;
  }, [cars]);

  const displayMaintenance = useMemo(() => {
    return maintenanceRequests;
  }, [maintenanceRequests]);

  const handleAssignBuilding = async () => {
    if (selectedBuildingIds.length === 0) return toast.error("Please select at least one building.");
    setAssigningBuilding(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/assign-buildings`, {
        method: "POST",
        body: JSON.stringify({
          estateId: estateId,
          tenantId: id,
          buildingIds: selectedBuildingIds,
          rentSchedule: Number(rentPaymentSchedule),
        }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to assign building.");
      toast.success("Buildings assigned successfully.");
      setSelectedBuildingIds([]);
      fetchDetails();
      if (window.bootstrap?.Modal && assignBuildingModalRef.current) window.bootstrap.Modal.getInstance(assignBuildingModalRef.current)?.hide();
    } catch (err) { toast.error(err.message); }
    finally { setAssigningBuilding(false); }
  };

  const toggleBuildingSelection = (bId) => {
    setSelectedBuildingIds(prev => prev.includes(bId) ? prev.filter(id => id !== bId) : [...prev, bId]);
  };

  const rentPaymentSchedule = 4;

  const handleOpenUnassignModal = () => {
    if (assignedBuildings.length > 0) {
      setSelectedUnassignBuildingId(assignedBuildings[0].id || assignedBuildings[0].buildingId || "all");
    }
    if (window.bootstrap?.Modal && unassignAllModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(unassignAllModalRef.current).show();
    }
  };

  const handleExecuteUnassignSubmit = async () => {
    if (!selectedUnassignBuildingId) return;
    setUnassigningAll(true);
    try {
      if (selectedUnassignBuildingId === "all") {
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/unassign-buildings`, { method: "PATCH", body: JSON.stringify({}) });
        if (!res.ok) throw new Error("Failed to unassign all buildings.");
        toast.success("All buildings unassigned successfully.");
      } else {
        const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/buildings/${selectedUnassignBuildingId}/unassign`, { method: "PATCH", body: JSON.stringify({}) });
        if (!res.ok) throw new Error("Failed to unassign building.");
        toast.success("Building unassigned successfully.");
      }
      if (window.bootstrap?.Modal && unassignAllModalRef.current) {
        window.bootstrap.Modal.getInstance(unassignAllModalRef.current)?.hide();
      }
      fetchDetails();
    } catch (err) {
      toast.error(err.message || "Failed to unassign building.");
    } finally {
      setUnassigningAll(false);
    }
  };

  const handleSaveCar = async () => {
    if (!carForm.make || !carForm.model || !carForm.year) { toast.error("Please fill in Make, Model, and Year."); return; }
    setSavingCar(true);
    try {
      const imageUrl = getCarImageUrl(carForm.make, carForm.model, carForm.year, carForm.color);
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/cars`, {
        method: "POST",
        body: JSON.stringify({ manufacturer: carForm.make, make: carForm.make, model: carForm.model, year: Number(carForm.year), color: carForm.color, licensePlate: carForm.licensePlate, imageUrl, drivers: carDrivers }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to save car.");
      toast.success("Car added successfully.");
      setCarForm({ make: "", model: "", year: "", color: "", licensePlate: "" });
      setCarDrivers([]);
      fetchDetails();
      if (window.bootstrap?.Modal && addCarModalRef.current) window.bootstrap.Modal.getInstance(addCarModalRef.current)?.hide();
    } catch (err) { toast.error(err.message); }
    finally { setSavingCar(false); }
  };

  const toggleDriverSelection = (dependant, accessType) => {
    setCarDrivers(prev => {
      const exists = prev.find(d => d.id === dependant.id);
      if (exists) return prev.filter(d => d.id !== dependant.id);
      return [...prev, { ...dependant, accessType }];
    });
  };

  const handleTerminate = async () => {
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/terminate`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to terminate contract.");
      toast.success("Tenant contract terminated.");
      navigate("/tenants");
    } catch (err) { toast.error(err.message); }
  };

  const handleRemovePrimaryOccupant = async (occupantId, bId) => {
    if (!(await confirmDialog({ title: "Remove Primary Occupant", message: "Are you sure you want to remove this primary occupant?", confirmLabel: "Remove" }))) return;
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${id}/primary-occupants/${occupantId}/buildings/${bId}/unassign`, { method: "PATCH", body: JSON.stringify({}) });
      if (!res.ok) throw new Error("Failed to remove occupant.");
      toast.success("Primary occupant removed.");
      fetchDetails();
    } catch (err) { toast.error(err.message); }
  };

  // Invoice status badge helper
  const invoiceStatusBadge = (status) => ({
    Paid: "badge-light-success",
    Created: "badge-light-warning",
    Overdue: "badge-light-danger",
    Cancelled: "badge-light-secondary",
  }[status] || "badge-light-secondary");

  // Payment status badge helper
  const paymentStatusBadge = (status) => ({
    Paid: "badge-light-success",
    Successful: "badge-light-success",
    New: "badge-light-info",
    Pending: "badge-light-warning",
    Failed: "badge-light-danger",
    Reversed: "badge-light-warning",
  }[status] || "badge-light-secondary");

  const fullName = toTitleCase(`${tenant?.firstName || ""} ${tenant?.lastName || ""}`.trim()) || "Corporate Tenant";
  const titlePrefix = tenant?.title ? `${tenant.title} ` : "";
  const displayTitleName = `${titlePrefix}${fullName}`.trim();
  const initials = getInitials(fullName);
  const assignedBuildings = tenant?.assignedBuildings || (tenant?.assignedBuilding ? [tenant.assignedBuilding] : []);
  const hasDue = (tenant?.outstandingInvoices || []).some(i => i.status !== "Paid");
  const inEstate = tenant?.inEstate ?? true;
  const carPreviewUrl = carForm.make && carForm.model ? getCarImageUrl(carForm.make, carForm.model, carForm.year, carForm.color) : null;

  let totalBasicRent = 0;
  let totalServiceCharge = 0;

  if (assignedBuildings.length > 0) {
    assignedBuildings.forEach((b) => {
      const bRentObj = b.currentRent || b.rent || {};
      const bRent = Number(
        bRentObj.rentAmount ||
        b.rentAmount ||
        b.basicRent ||
        b.outstandingInvoices?.[0]?.rentAmount ||
        0
      );

      const bTotal = Number(
        b.outstandingInvoices?.[0]?.totalAmount ||
        b.outstandingInvoices?.[0]?.invoiceTotalAmount ||
        bRentObj.totalAmount ||
        b.totalAmount ||
        0
      );

      let bSvc = 0;
      if (bTotal > bRent) {
        bSvc = bTotal - bRent;
      } else {
        const bId = b.id || b.buildingId;
        if (bId) {
          const stored = localStorage.getItem(`zangon_building_charges_${bId}`);
          if (stored) {
            const parsed = safeJsonParse(stored);
            if (Array.isArray(parsed)) {
              bSvc = parsed.reduce((sum, c) => sum + Number(c.amount || 0), 0);
            }
          }
        }
        if (!bSvc && b.serviceCharge) {
          bSvc = Number(b.serviceCharge || 0);
        }
      }

      totalBasicRent += bRent;
      totalServiceCharge += bSvc;
    });
  } else {
    const activeRentObj = tenant?.currentRent;
    totalBasicRent = Number(
      activeRentObj?.rentAmount ||
      rentInvoices?.[0]?.rentAmount ||
      0
    );
    const totalAmt = Number(
      rentInvoices?.[0]?.totalAmount ||
      rentInvoices?.[0]?.invoiceTotalAmount ||
      activeRentObj?.totalAmount ||
      0
    );
    if (totalAmt > totalBasicRent) {
      totalServiceCharge = totalAmt - totalBasicRent;
    }
  }

  const rentAmt = totalBasicRent;
  const svcAmt = totalServiceCharge;

  const renderHouseCard = (b, idx, withOccupant = false) => {
    const houseImg = b.imageUrl || getHouseImage(b.typeName || b.buildingTypeName || b.name);
    const phaseName = b.phaseName || b.phase || "";
    const typeName = b.typeName || b.buildingTypeName || "Assigned Property";
    const bNumStr = String(b.number || b.buildingNumber || b.houseNumber || (withOccupant ? idx + 1 : "")).trim();
    const bId = b.id || b.buildingId;
    const rentVal = Number(b.currentRent?.rentAmount || b.rentAmount || b.basicRent || b.outstandingInvoices?.[0]?.rentAmount || 0);
    const bTotal = Number(b.outstandingInvoices?.[0]?.totalAmount || b.outstandingInvoices?.[0]?.invoiceTotalAmount || b.currentRent?.totalAmount || b.totalAmount || 0);
    const serviceCharge = bTotal > rentVal ? bTotal - rentVal : Number(b.serviceCharge || 0);
    const assignedOn = b.assignedAt || b.assignedDate || b.dateAssigned || b.createdAt || b.createdDate || tenant?.createdAt;
    const isDueHouse = (b.outstandingInvoices || []).some(i => i.status !== "Paid") || !!(b.isRentDue || b.isDue);
    const matchingOcc = primaryOccupants.find(occ =>
      (occ.currentBuildingId && occ.currentBuildingId === bId) ||
      (occ.buildingId && occ.buildingId === bId) ||
      (occ.assignedBuildingId && occ.assignedBuildingId === bId) ||
      (occ.currentBuildingNumber && bNumStr && String(occ.currentBuildingNumber).trim() === bNumStr)
    );
    const targetOccId = matchingOcc?.id || "unassigned";
    return (
      <div
        className={`d6-card h-100 d-flex flex-column ${isDueHouse ? "border-hover-danger" : "border-hover-success"} transition-all overflow-hidden cursor-pointer`}
        onClick={() => navigate(`/tenants/${id}/occupants/${targetOccId}?buildingId=${bId}`)}
      >
        <div className="position-relative overflow-hidden" style={{ height: 180 }}>
          <img
            alt={typeName}
            src={houseImg}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { e.target.src = "/assets/media/zangon/3bedroomsbungalo.jpg"; }}
          />
          <div className="position-absolute" style={{ inset: 0, background: "linear-gradient(rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%)" }} />
          <div className="position-absolute bottom-0 start-0 p-3 d-flex align-items-center gap-2">
            {bNumStr && <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">{bNumStr}</span>}
            {phaseName && <span className="badge bg-success text-white fs-8 fw-semibold rounded-pill px-3 py-1">{phaseName}</span>}
          </div>
        </div>
        <div className="p-4 d-flex flex-column flex-grow-1">
          <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div className="min-w-0">
              <h4 className="d6-title text-gray-900 mb-1 text-truncate">{typeName}</h4>
              <span className="d6m-muted fs-8">{assignedOn ? `Assigned ${fmtDate(assignedOn)}` : "Assigned house"}</span>
            </div>
            <StatusChip color={isDueHouse ? "#DC2626" : "#16A34A"}>{isDueHouse ? "Due" : "Active"}</StatusChip>
          </div>
          {withOccupant && (
            <div className="d6m-muted fs-8 mb-3 text-truncate">
              Primary occupant: <strong className="text-gray-900">{matchingOcc ? toTitleCase(`${matchingOcc.title ? matchingOcc.title + " " : ""}${matchingOcc.firstName || ""} ${matchingOcc.lastName || ""}`.trim()) : (b.primaryOccupantName || b.occupantName || "Not assigned")}</strong>
            </div>
          )}
          <div className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top border-gray-100">
            <div className="d-flex flex-column min-w-0">
              <span className="d6m-k mb-0 fs-9">Base Rent</span>
              <div className="d-flex align-items-baseline gap-2">
                <span className="d6-title text-success fs-7 mb-0">{rentVal > 0 ? `₦${rentVal.toLocaleString()}` : "—"}</span>
                {serviceCharge > 0 && <span className="text-gray-400 fs-9 text-truncate">+₦{serviceCharge.toLocaleString()} Service Charge</span>}
              </div>
            </div>
            <span className="d6-icon-btn"><i className="ki-outline ki-arrow-right fs-5 text-gray-700" /></span>
          </div>
        </div>
      </div>
    );
  };

  const renderCarCard = (car, idx) => {
    const imgUrl = car.imageUrl || getCarImageUrl(car.make, car.model, car.year, car.color);
    const carName = car.manufacturer && car.make && car.manufacturer !== car.make
      ? `${car.manufacturer} ${car.make}`
      : (car.make && car.model && car.make !== car.model
          ? `${car.make} ${car.model}`
          : (car.manufacturer || car.make || car.model || "Vehicle"));
    const plate = car.licensePlate || "No Plate";
    const drivers = Array.isArray(car.assignedDependents) ? car.assignedDependents : [];
    const driversCount = car.assignedDependentsCount || drivers.length || 0;
    return (
      <div className="d6-card h-100 d-flex flex-column border-hover-success transition-all p-5 position-relative overflow-hidden cursor-pointer" onClick={() => setViewDriversCar(car)}>
        <div className="position-relative rounded-3 bg-light overflow-hidden d-flex align-items-center justify-content-center border border-gray-200 mb-4" style={{ height: 160 }}>
          {imgUrl ? (
            <img alt={carName} src={imgUrl} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", padding: 10 }} onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }} />
          ) : (
            <i className="ki-outline ki-car fs-2x text-gray-400"></i>
          )}
          <div className="position-absolute bottom-0 start-0 p-3">
            <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">{plate}</span>
          </div>
        </div>
        <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
          <div className="min-w-0">
            <h4 className="d6-title text-gray-900 mb-1 text-truncate">{carName}</h4>
            <span className="d6m-muted fs-8">{[car.year, car.color].filter(Boolean).join(" • ") || "Registered vehicle"}</span>
          </div>
          <StatusChip color="#0EA84A">Registered</StatusChip>
        </div>
        <div className="p-3 bg-light rounded-3 mt-auto">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center flex-shrink-0">
              {drivers.length === 0 ? (
                <span className="d6m-tile" style={{ width: 36, height: 36, borderRadius: 999 }}><i className="ki-outline ki-user" /></span>
              ) : (
                drivers.slice(0, 4).map((dr, di) => (
                  <span key={dr.dependentId || di} title={dr.fullName} style={{ marginLeft: di > 0 ? -10 : 0, zIndex: 10 - di, borderRadius: "50%", border: "2px solid #f4f4f5", display: "inline-flex" }}>
                    <Avatar src={dr.imageUrl} name={dr.fullName} size={36} />
                  </span>
                ))
              )}
            </div>
            <div className="d-flex flex-column min-w-0">
              <span className="d6m-name fs-8 text-truncate">{drivers.length > 0 ? drivers.map(dr => dr.fullName).join(", ") : "No drivers linked"}</span>
              <span className="d6m-muted fs-9">{driversCount} driver(s) assigned</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady || loading} />

      {/* Invoice detail / payment start modal */}
      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => { setSelectedInvoice(null); fetchActivityTab(0); }}
          onSuccess={() => {
            fetchActivityTab(0);
            fetchActivityTab(1);
          }}
        />
      )}
      {selectedPayment && (
        <PaymentReceiptModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
      {selectedAccess && (
        <AccessDetailsModal
          person={selectedAccess} estateId={estateId} tenantId={id} tenant={tenant}
          onClose={() => setSelectedAccess(null)}
          onDelete={handleDeleteDependant}
          onRefreshList={fetchDetails}
        />
      )}

      {viewDriversCar && (
        <ViewCarDriversModal car={viewDriversCar} estateId={estateId} tenantId={id} tenant={tenant} onClose={() => setViewDriversCar(null)} onDelete={handleDeleteCar} />
      )}

      <RequestDetailModal
        modalRef={requestDetailModalRef} estateId={estateId} request={selectedRequest} tenant={tenant}
        onClose={() => setSelectedRequest(null)} onStatusChange={handleStatusChange}
      />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">

          <div id="kt_app_header" className="app-header" data-kt-sticky="true" style={{ position: "sticky", top: 0, zIndex: 100 }}>
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <Link to="/tenants"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>
              <div className="d-flex flex-stack flex-lg-row-fluid">
                <div className="page-title gap-4 me-3 mb-0">
                  <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-2">
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1"><Link to="/dashboard" className="text-gray-700 text-hover-primary me-1"><i className="ki-outline ki-home text-gray-700 fs-6"></i></Link></li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1"><Link to="/tenants" className="text-gray-700 text-hover-primary">Tenants</Link></li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">{fullName}</li>
                  </ul>
                  <h1 className="text-gray-900 d6-page-title m-0">Tenant Profile</h1>
                </div>
            <div className="d-flex gap-2 flex-wrap">
  {/* Only display at the top when buildings exist AND no primary occupant has been added yet */}
  {assignedBuildings.length >= 2 && primaryOccupants.length === 0 && (
    <button
      type="button"
      className="btn btn-light d6-mbtn"
      onClick={() => primaryOccupantModal.show()}
    >
      <i className="ki-outline ki-user-square fs-4 me-2"></i>Add Primary Occupant
    </button>
  )}

  {assignedBuildings.length === 1 && (
    <>
      <button className="btn btn-success d6-mbtn" data-bs-toggle="modal" data-bs-target="#kt_modal_assign_building">
        <i className="ki-outline ki-home fs-4 me-2"></i>Assign House
      </button>
      <button
        type="button"
        className="btn btn-danger d6-mbtn"
        onClick={handleOpenUnassignModal}
      >
        <i className="ki-outline ki-trash fs-4 me-2"></i>Unassign House
      </button>
    </>
  )}

  <button className="btn btn-success d6-mbtn" onClick={() => editTenantModal.show()}>
    <i className="ki-outline ki-pencil fs-4 me-2"></i>Edit Tenant
  </button>
</div>
              </div>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="row g-6">

                      {/* LEFT COLUMN — Profile & Total Rent Summary (Sticky 100% Screen Height) */}
                      <div className="col-12 col-xl-4">
                        <div className="d6-card overflow-hidden" style={{ position: "sticky", top: "90px", minHeight: "calc(100vh - 110px)", height: "calc(100vh - 110px)" }}>
                          <div className="card-body d-flex flex-column overflow-y-auto h-100 p-6">
                            {/* Tenant Profile Banner */}
                            <div className="d6-kpi-brand flex-shrink-0 p-6 mb-6 mx-n6 mt-n6 text-white" style={{ borderRadius: 0, minHeight: 112 }}>
                              <div className="d-flex align-items-center gap-4">
                                <div
                                  className="bg-transparent d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle overflow-hidden border border-2 border-gray-200"
                                  style={{ width: "65px", height: "65px", minWidth: "65px", minHeight: "65px" }}
                                >
                                  {tenant?.imageUrl ? (
                                    <img
                                      src={tenant.imageUrl}
                                      alt={displayTitleName}
                                      className="rounded-circle"
                                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        if (e.currentTarget.nextElementSibling) {
                                          e.currentTarget.nextElementSibling.style.display = "flex";
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <span
                                    className="fw-bold fs-2 text-primary bg-light-primary w-100 h-100 align-items-center justify-content-center rounded-circle"
                                    style={{ display: tenant?.imageUrl ? "none" : "flex" }}
                                  >
                                    {initials}
                                  </span>
                                </div>
                                <div className="d-flex flex-column">
                                  <span className="d6-title text-white fs-3">{displayTitleName}</span>
                                  <div className="mt-1 d-flex align-items-center gap-2">
                                    <span className="badge bg-white text-success fw-bold fs-8 rounded-pill px-3">
                                      {inEstate ? "In Estate" : "Out of Estate"}
                                    </span>
                                    <span className="badge bg-white bg-opacity-25 text-white fw-bold fs-8 rounded-pill px-3">
                                      {tenant?.isCorporateTenant ? "Corporate" : "Individual"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex flex-column gap-4">
                              {tenant?.isCorporateTenant && contactPersons && contactPersons.length > 0 && (contactPersons[0].firstName || contactPersons[0].lastName) && (
                                <>
                                  <div className="d-flex flex-stack">
                                    <div className="fw-semibold text-gray-600">Contact Person</div>
                                    <div className="fw-bold text-gray-900 text-end">
                                      {toTitleCase(`${contactPersons[0].firstName || ""} ${contactPersons[0].lastName || ""}`.trim())}
                                    </div>
                                  </div>
                                  {contactPersons[0].jobTitle && contactPersons[0].jobTitle !== "—" && (
                                    <div className="d-flex flex-stack">
                                      <div className="fw-semibold text-gray-600">Position</div>
                                      <div className="fw-bold text-gray-900 text-end">{contactPersons[0].jobTitle}</div>
                                    </div>
                                  )}
                                </>
                              )}
                              {tenant?.phoneNumber && tenant.phoneNumber !== "—" && (
                                <div className="d-flex flex-stack">
                                  <div className="fw-semibold text-gray-600">Phone</div>
                                  <div className="fw-bold text-gray-900 text-end">{tenant.phoneNumber}</div>
                                </div>
                              )}
                              {tenant?.email && tenant.email !== "—" && (
                                <div className="d-flex flex-stack">
                                  <div className="fw-semibold text-gray-600">Email</div>
                                  <div className="fw-bold text-gray-900 text-end text-break">{tenant.email}</div>
                                </div>
                              )}
                              {(tenant?.createdAt || tenant?.createdDate) && (
                                <div className="d-flex flex-stack">
                                  <div className="fw-semibold text-gray-600">Date Joined</div>
                                  <div className="fw-bold text-gray-900 text-end">{fmtDateLong(tenant.createdAt || tenant.createdDate)}</div>
                                </div>
                              )}

                              {tenant?.emergencyContact && (tenant.emergencyContact.name || tenant.emergencyContact.phone) && (
                                <div className="d6m-invoice mt-2">
                                  <div className="card-body py-3 px-4">
                                    <div className="fw-bold text-gray-900 mb-1 fs-7">Emergency Contact</div>
                                    {tenant.emergencyContact.name && <div className="text-gray-700 fs-7">{tenant.emergencyContact.name}{tenant.emergencyContact.relationship && <span className="text-gray-500"> ({tenant.emergencyContact.relationship})</span>}</div>}
                                    {tenant.emergencyContact.phone && <div className="text-gray-600 fs-7">{tenant.emergencyContact.phone}</div>}
                                  </div>
                                </div>
                              )}

                              <div className="separator my-4"></div>

                              {/* Total Rent Summary */}
                              <div className="d6m-section-head"><span>Rent summary</span></div>

                              <div className="row g-3">
                                {/* Single Assigned Building (shown inside Rent Summary when tenant has 1 building) */}
                                {assignedBuildings.length === 1 && (() => {
                                  const b = assignedBuildings[0];
                                  return <div className="col-12">{renderHouseCard(b, 0)}</div>;
                                })()}

                                <div className="col-6">
                                  <div className="d6m-kv-cell h-100">
                                    <span className="text-gray-600 fs-7 fw-bold d-block mb-1">
                                      Basic Rent {assignedBuildings.length > 1 ? `(${assignedBuildings.length} houses)` : ""}
                                    </span>
                                    <span className="text-gray-900 fw-bold fs-5">
                                      {rentAmt > 0 ? fmtMoney(rentAmt) : "—"}
                                    </span>
                                  </div>
                                </div>

                                <div className="col-6">
                                  <div className="d6m-kv-cell h-100">
                                    <span className="text-gray-600 fs-7 fw-bold d-block mb-1">
                                      Service Charge {assignedBuildings.length > 1 ? `(${assignedBuildings.length} houses)` : ""}
                                    </span>
                                    <span className="text-gray-900 fw-bold fs-5">
                                      {svcAmt > 0 ? fmtMoney(svcAmt) : "—"}
                                    </span>
                                  </div>
                                </div>
                                <div className="col-12">
                                  <div className={`p-4 rounded-3 h-100 ${hasDue ?"bg-light-danger border border-danger border-opacity-25" : "bg-light-success border border-success border-opacity-25"}`}>
                                    <span className={`fs-7 fw-bold d-block mb-1 ${hasDue ?"text-danger" : "text-success"}`}>
                                      Payment Status
                                    </span>
                                    <span className="text-gray-900 fw-bold fs-5">
                                      {hasDue ? "Has Outstanding" : "Fully Paid"}
                                    </span>
                                  </div>
                                </div>
                              </div>

                            

                              <div className="separator my-4"></div>

                              {/* Terminate Tenant Contract */}
                              <div className="d6m-section-head"><span>Terminate tenant's contract</span></div>
                              <div className="d6m-note d6m-note-danger d-flex flex-column p-4 mt-auto mb-0">
                                <div className="d-flex align-items-center mb-3">
                                  <div className="symbol symbol-40px symbol-circle bg-danger text-white d-flex align-items-center justify-content-center me-4 flex-shrink-0">
                                    <i className="ki-outline ki-information-5 fs-2 text-white"></i>
                                  </div>
                                  <div className="d-flex flex-column">
                                    <span className="fs-7 text-gray-700">This action will revoke access permissions, disable tenant profile, and may affect rent history reporting. Only proceed if you are sure.</span>
                                  </div>
                                </div>

                                {!terminateConfirm ? (
                                  <button 
                                    type="button" 
                                    className="btn d6-mbtn btn-danger w-100 fw-bold mt-2"
                                    onClick={() => setTerminateConfirm(true)}
                                  >
                                    <i className="ki-outline ki-trash fs-5 me-2"></i>
                                    Terminate Tenant's Contract
                                  </button>
                                ) : (
                                  <div className="mt-3 text-center">
                                    <div className="fw-semibold text-danger fs-8 mb-2">Are you sure? This cannot be undone.</div>
                                    <div className="d-flex gap-2">
                                      <button 
                                        type="button" 
                                        className="btn d6-mbtn btn-danger flex-grow-1 fs-8 fw-bold" 
                                        onClick={handleTerminate}
                                      >
                                        Yes, Terminate
                                      </button>
                                      <button 
                                        type="button" 
                                        className="btn d6-mbtn btn-light flex-grow-1 fs-8 fw-bold" 
                                        onClick={() => setTerminateConfirm(false)}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* RIGHT COLUMN */}
                      <div className="col-12 col-xl-8">
                        {/* Houses Owned / Assigned — only shown on right column when tenant has 0 or multiple (>1) buildings */}
                        {assignedBuildings.length !== 1 && (
                          <div className="d6-card p-6 mb-5">
<CardHead title={`Assigned Buildings (${assignedBuildings.length})`} subtitle="Properties linked to this tenant" className="mb-4">
                                <button className="btn d6-mbtn btn-success" data-bs-toggle="modal" data-bs-target="#kt_modal_assign_building">
                                  <i className="ki-outline ki-home fs-4 me-2"></i>Assign House
                                </button>
                                {assignedBuildings.length > 0 && (
                                  <button
                                    type="button"
                                    className="btn d6-mbtn btn-danger"
                                    onClick={handleOpenUnassignModal}
                                  >
                                    <i className="ki-outline ki-trash fs-4 me-2"></i>Unassign House
                                  </button>
                                )}
                              </CardHead>
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                              {assignedBuildings.length === 0 ? (
                                <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-4">
                                  <div className="fw-semibold">
                                    <div className="fs-6 text-gray-700">This tenant is not assigned to any property.</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="row g-5 pb-4">
                                  {assignedBuildings.map((b, idx) => (
                                    <div key={b.id || b.buildingId || idx} className="col-12 col-md-6">{renderHouseCard(b, idx, true)}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ── Rent Invoices Card ── */}
                        {displayInvoices.length > 0 && (
                          <div className="d6-card p-6 mb-5">
<CardHead title={`Rent Invoices (${displayInvoices.length})`} subtitle="Billing and invoice records" className="mb-4" />
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                              <div className="d-flex flex-column gap-3 pb-4">
                                {displayInvoices.map((inv, i) => {
                                  const isPaid = inv.status === "Paid";
                                  const isOverdue = inv.status === "Overdue" || (!isPaid && inv.dueDate && new Date(inv.dueDate) < new Date());

                                  return (
                                    <div
                                      key={inv.id || i}
                                      className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all border-hover-success position-relative overflow-hidden"
                                      style={{ minHeight: 84 }}
                                      onClick={() => setSelectedInvoice(inv)}
                                    >
                                      <div className={`position-absolute top-0 start-0 h-100 w-4px ${isPaid ?"bg-success" : isOverdue ? "bg-danger" : "bg-warning"}`}></div>
                                      <div className="d-flex align-items-center me-4" style={{ width: "360px", flexShrink: 0 }}>
                                        <div className="symbol symbol-45px symbol-circle me-4 flex-shrink-0">
                                          <span className={`symbol-label rounded-circle w-45px h-45px fw-bold fs-5 ${isPaid ?"bg-light-success text-success" : isOverdue ? "bg-light-danger text-danger" : "bg-light-warning text-warning"}`}>
                                            <i className={`ki-outline ${isPaid ?"ki-check-circle text-success" : isOverdue ? "ki-file text-danger" : "ki-file text-warning"} fs-4`}></i>
                                          </span>
                                        </div>
                                        <div className="d-flex flex-column overflow-hidden">
                                          <span className="fs-6 fw-bold text-gray-900 text-truncate mb-1">{inv.invoiceNumber || `Rent Invoice #${i + 1}`}</span>
                                          <div className="text-gray-500 fs-7 text-truncate">
                                            {inv.periodStart ? fmtDate(inv.periodStart) : "Period"} {inv.periodEnd ? `→ ${fmtDate(inv.periodEnd)}` : ""}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-6 text-end ms-auto flex-shrink-0">
                                        <div className="d-none d-sm-block text-center ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Due Date</div>
                                          <div className={`fw-semibold fs-7 ${isOverdue ?"text-danger fw-bold" : "text-gray-900"}`}>
                                            {inv.dueDate ? fmtDate(inv.dueDate) : "—"}
                                          </div>
                                        </div>
                                        <div className="text-end ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Amount</div>
                                          <div className="fw-bold text-gray-900 fs-6">{fmtMoney(inv.totalAmount || inv.rentAmount)}</div>
                                        </div>

                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ── Payment History Card ── */}
                        {displayPayments.length > 0 && (
                          <div className="d6-card p-6 mb-5">
<CardHead title={`Payment History (${displayPayments.length})`} subtitle="Track transaction and receipt history" className="mb-4" />
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                              <div className="d-flex flex-column gap-3 pb-4">
                                {displayPayments.map((p, i) => (
                                  <div
                                    key={p.id || i}
                                    className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all border-hover-success position-relative overflow-hidden"
                                    style={{ minHeight: 84 }}
                                    onClick={() => {
                                      if (p.receiptUrl) window.open(p.receiptUrl, "_blank");
                                      else setSelectedPayment(p);
                                    }}
                                  >

                                    <div className="d-flex align-items-center me-4" style={{ width: "360px", flexShrink: 0 }}>
                                      <div className="symbol symbol-45px symbol-circle me-4 flex-shrink-0">
                                        <span className="symbol-label rounded-circle w-45px h-45px fw-bold fs-4 bg-light-success text-success">
                                          ₦
                                        </span>
                                      </div>
                                      <div className="d-flex flex-column overflow-hidden">
                                        <span className="fs-6 fw-bold text-gray-900 text-truncate mb-1">{p.reference || `Payment Ref #${i + 1}`}</span>
                                        <div className="text-gray-500 fs-7 text-truncate">
                                          {p.paymentMethod || "Bank Transfer"} {p.buildingNumber ? `· Building ${p.buildingNumber}` : ""}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-6 text-end ms-auto flex-shrink-0">
                                      <div className="d-none d-sm-block text-center ms-4">
                                        <div className="text-gray-500 fs-8 mb-1">Date</div>
                                        <div className="fw-semibold text-gray-900 fs-7">{fmtDate(p.paidAt || p.createdDate)}</div>
                                      </div>
                                      <div className="text-end ms-4">
                                        <div className="text-gray-500 fs-8 mb-1">Amount Paid</div>
                                        <div className="fw-bold text-success fs-6">{fmtMoney(p.amountPaid || p.amount || p.rentAmount)}</div>
                                      </div>

                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Access List */}
                        {totalAccessCount > 0 && (
                          <div className="d6-card p-6 mb-5">
<CardHead title={`Dependants (${totalAccessCount})`} subtitle="Registered family members and guests" className="mb-4">
                                <button className="btn d6-mbtn btn-light-success" data-bs-toggle="modal" data-bs-target="#kt_modal_add_dependant">
                                  <i className="ki-outline ki-plus fs-4 me-2"></i>Add
                                </button>
                              </CardHead>
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                              <div className="d-flex flex-column gap-3 pb-4">
                                {activeAccessList.map((a, i) => renderAccessRowItem(a, i, false))}
                              </div>

                              {inactiveAccessList.length > 0 && (
                                <div className="accordion accordion-flush mt-6" id="inactiveAccessAccordion">
                                  <div className="border border-gray-200 rounded-3 p-4 bg-light">
                                    <div 
                                      className="d-flex align-items-center justify-content-between cursor-pointer user-select-none collapsed"
                                      data-bs-toggle="collapse"
                                      data-bs-target="#collapseInactiveAccess"
                                      aria-expanded="false"
                                      aria-controls="collapseInactiveAccess"
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <i className="ki-outline ki-down fs-4 text-gray-600 transition-all"></i>
                                        <span className="fs-6 fw-bold text-gray-800">Inactive / Expired Access ({inactiveAccessList.length})</span>
                                      </div>
                                      <span className="badge badge-light-danger fw-bold fs-8">Inactive</span>
                                    </div>
                                    <div id="collapseInactiveAccess" className="collapse mt-4">
                                      <div className="d-flex flex-column gap-3">
                                        {inactiveAccessList.map((a, i) => renderAccessRowItem(a, i, true))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Primary Occupants */}
                        {primaryOccupants.length > 0 && (
                          <div className="d6-card p-6 mb-5">
<CardHead title={`Primary Occupants (${primaryOccupants.length})`} subtitle="Resident occupant profiles" className="mb-4">
                                {assignedBuildings.length >= 2 && (
                                <button className="btn d6-mbtn btn-success me-2" onClick={() => primaryOccupantModal.show()}>
                                  <i className="ki-outline ki-plus fs-4 me-2"></i>Add Occupant
                                </button>
                                )}
                                 <button className="btn d6-mbtn btn-danger">
                                  <i className="ki-outline ki-plus fs-4 me-2"></i>Delete Occupant
                                </button>
                              </CardHead>
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                              <div className="d-flex flex-column gap-3 pb-4">
                                {primaryOccupants.map((occ, idx) => {
                                  const occTitleStr = occ.title ? `${occ.title} ` : "";
                                  const occName = toTitleCase(`${occTitleStr}${occ.firstName || ""} ${occ.lastName || ""}`.trim()) || toTitleCase(occ.name) || "Primary Occupant";
                                  const initials = getInitials(occName);
                                  const houseText = occ.currentBuildingNumber
                                    ? `${occ.currentPhaseName ? occ.currentPhaseName + " — " : ""}Building ${occ.currentBuildingNumber}`
                                    : "No House Assigned";
                                  const phone = occ.phoneNumber || occ.email || "—";
                                  const carCount = occ.carCount ?? 0;
                                  const dependentCount = occ.dependentCount ?? 0;

                                  return (
                                    <div
                                      key={occ.id || idx}
                                      className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all border-hover-success position-relative overflow-hidden"
                                      style={{ minHeight: 84 }}
                                      onClick={() => navigate(`/tenants/${id}/occupants/${occ.id}`)}
                                    >
                                      <div className="d-flex align-items-center me-4" style={{ width: "360px", flexShrink: 0 }}>
                                        <div className="symbol symbol-45px symbol-circle me-4 flex-shrink-0">
                                          {occ.imageUrl ? (
                                            <img src={occ.imageUrl} alt={occName} className="object-fit-cover rounded-circle w-45px h-45px" onError={(e) => { e.target.style.display = "none"; }} />
                                          ) : (
                                            <span className="symbol-label rounded-circle w-45px h-45px fw-bold fs-5 bg-light-success text-success">{initials}</span>
                                          )}
                                        </div>
                                        <div className="d-flex flex-column overflow-hidden">
                                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <span className="fs-6 fw-bold text-gray-900 text-truncate">{occName}</span>
                                            <span className="badge fs-8 fw-semibold badge-light-success text-success">Primary Occupant</span>
                                          </div>
                                          <div className="text-gray-500 fs-7 text-truncate" title={phone}>{phone}</div>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-6 text-end ms-auto flex-shrink-0">
                                        <div className="d-none d-sm-block text-center ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Assigned House</div>
                                          <div className="fw-semibold text-gray-900 fs-7">{houseText}</div>
                                        </div>
                                        <div className="d-none d-sm-block text-center ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Cars</div>
                                          <div className="fw-bold text-success fs-7">{carCount}</div>
                                        </div>
                                        <div className="text-center ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Dependents</div>
                                          <div className="fw-bold text-gray-900 fs-7">{dependentCount}</div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ── Maintenance Requests Card ── */}
                        {displayMaintenance.length > 0 && (
                          <div className="d6-card p-6 mb-5">
<CardHead title={`Maintenance Requests (${displayMaintenance.length})`} subtitle="Service and repair logs" className="mb-4" />
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
                              <div className="d-flex flex-column gap-3 pb-4">
                                {displayMaintenance.map((r, i) => {
                                  const isCompleted = r.status === "Completed" || r.status === "Resolved";
                                  const isInProgress = r.status === "In Progress";
                                  const isCanceled = r.status === "Canceled" || r.status === "Closed";

                                  const statusBgClass = isCompleted ? "bg-success"
                                    : isInProgress ? "bg-warning"
                                    : isCanceled ? "bg-secondary" : "bg-danger";

                                  const statusBadgeClass = isCompleted ? "badge-light-success text-success"
                                    : isInProgress ? "badge-light-warning text-warning"
                                    : isCanceled ? "badge-light-secondary text-gray-600" : "badge-light-danger text-danger";

                                  const buildingSubtext = r.buildingNumber
                                    ? (String(r.buildingNumber).toLowerCase().includes("building") ? r.buildingNumber : `Building ${r.buildingNumber}`)
                                    : (r.phaseName ? `${r.phaseName} - ${r.houseNumber || ""}` : (tenant?.buildingNumber ? `Building ${tenant.buildingNumber}` : "Unassigned"));

                                  return (
                                    <div
                                      key={r.id || i}
                                      className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 cursor-pointer transition-all border-hover-success position-relative overflow-hidden"
                                      style={{ minHeight: 84 }}
                                      onClick={() => handleOpenRequest(r)}
                                    >
                                      <div className={`position-absolute top-0 start-0 h-100 ${statusBgClass} w-4px`}></div>

                                      <div className="d-flex align-items-center me-4" style={{ width: "360px", flexShrink: 0 }}>
                                        <div className="symbol symbol-45px symbol-circle me-4 flex-shrink-0">
                                          <span className={`symbol-label rounded-circle w-45px h-45px fw-bold fs-5 ${statusBadgeClass}`}>
                                            <i className="ki-outline ki-wrench fs-4"></i>
                                          </span>
                                        </div>
                                        <div className="d-flex flex-column overflow-hidden">
                                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <span className="fs-6 fw-bold text-gray-900 text-truncate">
                                              {r.title || r.shortDescription || "Maintenance Request"}
                                            </span>

                                            <span
                                              className={`badge fs-8 fw-semibold ${
 r.category ==="Electrical"
                                                  ? "badge-light-warning text-warning"
                                                  : r.category === "Plumbing"
                                                  ? "badge-light-info text-info"
                                                  : r.category === "Cleaning"
                                                  ? "badge-light-success text-success"
                                                  : "badge-light-primary text-primary"
                                              }`}
                                            >
                                              {r.category || "General Maintenance"}
                                            </span>
                                          </div>

                                          <div className="text-gray-500 fs-7 text-truncate">
                                            {buildingSubtext}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="d-flex align-items-center gap-6 text-end ms-auto flex-shrink-0">
                                        <div className="d-none d-sm-block text-center ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Requested Date</div>
                                          <div className="fw-semibold text-gray-900 fs-7">{fmtDate(r.createdAt || r.createdDate)}</div>
                                        </div>
                                        <div className="d-none d-sm-block text-center ms-4">
                                          <div className="text-gray-500 fs-8 mb-1">Date Completed</div>
                                          <div className="fw-semibold text-gray-900 fs-7">{isCompleted ? fmtDate(r.completedAt || r.updatedAt || r.createdAt || r.createdDate) : "—"}</div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cars & Drivers */}
                        <div className="d6-card p-6 mb-5">
<CardHead title={`Cars & Drivers`} subtitle="Registered vehicles and driver details" className="mb-4">
                              <button type="button" className="btn d6-mbtn btn-success" data-bs-toggle="modal" data-bs-target="#kt_modal_add_car">
                                <i className="ki-outline ki-plus fs-4 me-2"></i>Add New Car
                              </button>
                            </CardHead>
<div className="overflow-y-auto" style={{ maxHeight: 520 }}>
  {displayCars.length === 0 ? (
    <div className="text-center py-5">
      <i className="ki-outline ki-car fs-3x text-gray-300 d-block mb-3"></i>
      <div className="fs-6 text-gray-500 mb-5">No cars registered.</div>
    </div>
  ) : (
    <div className="row g-5 pb-4">
      {displayCars.map((car, idx) => (
        <div key={car.id || idx} className="col-12 col-md-6">{renderCarCard(car, idx)}</div>
      ))}
    </div>
  )}
</div>
                        </div>
                      </div>
                    </div>
                    <div className="my-10"></div>
                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                  <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                    <div className="text-gray-900 text-center">
                      <span className="text-muted fw-semibold me-1">2026&copy;</span>
                      <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                    </div>
                  </div>
                </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up"></i>
          </div>
        </div>
      </div>

      <DownloadCarLogModal modalRef={downloadCarLogModalRef} cars={displayCars} />
      <EditTenantModal modalRef={editTenantModalRef} tenant={tenant} estateId={estateId} adminId={adminId} onSuccess={fetchDetails} />
      <PrimaryOccupantModal modalRef={primaryOccupantModalRef} tenant={tenant} estateId={estateId} buildings={assignedBuildings} primaryOccupants={primaryOccupants} onSaved={fetchDetails} />
      <UnassignSingleBuildingModal modalRef={unassignSingleBuildingModalRef} building={buildingToUnassign} estateId={estateId} tenantId={id} onUnassigned={fetchDetails} />

      {/* Assign Building Modal */}
      <ModalShell
        id="kt_modal_assign_building"
        modalRef={assignBuildingModalRef}
        icon="ki-home-2"
        title="Assign Building to Tenant"
        onClose={() => hideModal(assignBuildingModalRef)}
        closeDisabled={assigningBuilding}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(assignBuildingModalRef)}>Cancel</button>
            <button type="button" className="btn btn-success d6-mbtn" onClick={handleAssignBuilding} disabled={assigningBuilding || selectedBuildingIds.length === 0}>
              {assigningBuilding ? <><span className="spinner-border spinner-border-sm me-1"></span>Assigning...</> : "Assign House"}
            </button>
          </>
        }
      >
        <Field label="Search building / house number">
          <div className="position-relative">
            <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
            <input type="text" className="d6m-input ps-10" placeholder="Search buildings (e.g. 23)..." value={buildingSearchQuery} onChange={e => setBuildingSearchQuery(e.target.value)} />
          </div>
        </Field>

        <div className="d6m-section">
          <div className="d6m-section-head"><span>Select buildings</span><span className="d6m-count">{selectedBuildingIds.length}</span></div>
          {(() => {
            const list = assignableBuildings.filter(b => {
              if (!buildingSearchQuery) return true;
              const q = buildingSearchQuery.trim().toLowerCase();
              return String(b.number || b.buildingNumber || "").toLowerCase().includes(q)
                || String(b.phaseName || "").toLowerCase().includes(q)
                || String(b.typeName || "").toLowerCase().includes(q);
            });
            const sorted = sortBuildingsForAssignment(list, tenant ? [tenant] : []);
            if (sorted.length === 0) return <div className="d6m-empty">No buildings match search</div>;
            return (
              <div className="d6m-list" style={{ maxHeight: 280, overflowY: "auto" }}>
                {sorted.map(b => {
                  const bId = b.id || b.buildingId;
                  const isChecked = selectedBuildingIds.includes(bId);
                  const isAssigned = checkIsBuildingAssigned(b, tenant ? [tenant] : []);
                  const assignedTenantName = b.currentTenantName || b.tenantName || b.occupantName;
                  return (
                    <button
                      type="button"
                      key={bId}
                      className={`d6m-list-row ${isChecked ? "selected" : ""}`}
                      style={isAssigned ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                      disabled={isAssigned}
                      onClick={() => { if (!isAssigned) toggleBuildingSelection(bId); }}
                    >
                      <span className="d6m-check" style={isChecked ? { background: "#0EA84A", borderColor: "#0EA84A" } : undefined}>
                        <svg viewBox="0 0 24 24" style={{ opacity: isChecked ? 1 : 0 }}><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                      <span className="min-w-0 flex-grow-1 text-start">
                        <span className="d6m-v d-block text-truncate">{b.number} — {b.phaseName}</span>
                        <span className="d6m-muted d-block text-truncate">{b.typeName || "House"}</span>
                      </span>
                      {isAssigned && <StatusChip color="#DC2626">{assignedTenantName ? `Assigned to ${assignedTenantName}` : "Assigned"}</StatusChip>}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {selectedBuildingIds.length > 0 && (() => {
          const selectedList = selectedBuildingIds.map(bid => assignableBuildings.find(x => (x.id || x.buildingId) === bid)).filter(Boolean);
          const first = selectedList[0];
          const firstImg = first ? (first.imageUrl || getHouseImage(first.typeName || first.name)) : null;
          return (
            <div className="d6m-section">
              <div className="d6m-section-head"><span>Assignment preview</span></div>
              {firstImg && (
                <div className="position-relative overflow-hidden mb-3" style={{ height: 160, borderRadius: 14 }}>
                  <img src={firstImg} alt="Selected House" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <div className="position-absolute bottom-0 start-0 p-3">
                    <span className="badge bg-white text-gray-900 fw-bold rounded-pill px-3 py-1">
                      {selectedList.length === 1 ? `${first.number} — ${first.phaseName}` : `${first.number} (+${selectedList.length - 1} more)`}
                    </span>
                  </div>
                </div>
              )}
              <div className="d6m-kv d6m-kv-3">
                <div className="d6m-kv-cell"><span className="d6m-k">Tenant</span><span className="d6m-v">{fullName}</span></div>
                <div className="d6m-kv-cell"><span className="d6m-k">Schedule</span><span className="d6m-v">Yearly</span></div>
                <div className="d6m-kv-cell"><span className="d6m-k">Buildings</span><span className="d6m-v">{selectedList.map(b => b.number).join(", ")}</span></div>
              </div>
            </div>
          );
        })()}
        <div className="d6m-note mt-4">
          <i className="ki-outline ki-information-5 fs-3"></i>
          <div>This assigns the selected building(s) to the tenant with a yearly payment schedule.</div>
        </div>
      </ModalShell>

      {/* Unassign House Selection Modal */}
      <ModalShell
        id="kt_modal_unassign_all_buildings"
        modalRef={unassignAllModalRef}
        icon="ki-trash"
        title="Unassign House"
        onClose={() => hideModal(unassignAllModalRef)}
        closeDisabled={unassigningAll}
        width={560}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(unassignAllModalRef)} disabled={unassigningAll}>Cancel</button>
            <button type="button" className="btn btn-danger d6-mbtn" onClick={handleExecuteUnassignSubmit} disabled={unassigningAll || !selectedUnassignBuildingId}>
              {unassigningAll
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Unassigning...</>
                : <><i className="ki-outline ki-trash fs-5 me-2"></i>{selectedUnassignBuildingId === "all" ? "Unassign All Houses" : "Unassign Selected House"}</>}
            </button>
          </>
        }
      >
        <div className="d6m-note mb-4">
          <i className="ki-outline ki-information-5 fs-3"></i>
          <div>Select which house to unassign from <strong>{fullName}</strong>, or choose to unassign all houses.</div>
        </div>
        <div className="d6m-list">
          {assignedBuildings.map((b, idx) => {
            const bId = b.id || b.buildingId;
            const houseImg = b.imageUrl || getHouseImage(b.typeName || b.buildingTypeName || b.name);
            const phaseName = b.phaseName || b.phase || "Phase";
            const typeName = b.typeName || b.buildingTypeName || "Property";
            const bNum = b.number || b.buildingNumber || b.houseNumber || (idx + 1);
            const isSelected = selectedUnassignBuildingId === bId;
            return (
              <button type="button" key={bId || idx} className={`d6m-list-row ${isSelected ? "selected" : ""}`} onClick={() => setSelectedUnassignBuildingId(bId)}>
                <Avatar src={houseImg} name={typeName} size={44} />
                <span className="min-w-0 flex-grow-1 text-start">
                  <span className="d6m-v d-block text-truncate">{typeName}</span>
                  <span className="d6m-muted d-block text-truncate">{phaseName} - {bNum}</span>
                </span>
                {isSelected && <i className="ki-outline ki-check-circle fs-2 text-success"></i>}
              </button>
            );
          })}
          {assignedBuildings.length > 1 && (
            <button type="button" className={`d6m-list-row ${selectedUnassignBuildingId === "all" ? "selected" : ""}`} onClick={() => setSelectedUnassignBuildingId("all")}>
              <span className="d6m-tile" style={{ width: 44, height: 44, borderRadius: 999 }}><i className="ki-outline ki-trash text-danger" /></span>
              <span className="min-w-0 flex-grow-1 text-start">
                <span className="d6m-v d-block text-danger">Unassign all houses ({assignedBuildings.length})</span>
                <span className="d6m-muted d-block">Remove all house assignments for this tenant</span>
              </span>
              {selectedUnassignBuildingId === "all" && <i className="ki-outline ki-check-circle fs-2 text-success"></i>}
            </button>
          )}
        </div>
      </ModalShell>

      {/* Add Dependant Modal */}
      <ModalShell
        id="kt_modal_add_dependant"
        modalRef={addDependantModalRef}
        icon="ki-profile-user"
        title="Add Dependant / Access"
        onClose={() => hideModal(addDependantModalRef)}
        closeDisabled={savingDependant}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(addDependantModalRef)} disabled={savingDependant}>Cancel</button>
            <button type="button" className="btn btn-success d6-mbtn" onClick={handleAddDependant} disabled={savingDependant}>
              {savingDependant ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : "Add Dependant"}
            </button>
          </>
        }
      >
        <div className="d6m-upload">
          <button type="button" className="d6m-upload-preview" onClick={() => dependantFileInputRef.current?.click()} aria-label="Upload photo">
            {dependantImagePreview ? <img src={dependantImagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
          </button>
          <div className="flex-grow-1 min-w-0">
            <div className="d6m-v">Dependant photo</div>
            <div className="d6m-muted">PNG or JPG, up to 5MB</div>
          </div>
          <div className="d-flex gap-2">
            {dependantImagePreview && <button type="button" className="btn btn-light-danger d6-mbtn" onClick={() => { setDependantImageFile(null); setDependantImagePreview(null); }}>Remove</button>}
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => dependantFileInputRef.current?.click()}>{dependantImagePreview ? "Change" : "Upload"}</button>
          </div>
          <input ref={dependantFileInputRef} type="file" accept="image/*" className="d-none" onChange={handleDependantImageSelect} />
        </div>

        <div className="d-flex flex-column gap-4 mt-5">
          <div className="row g-4">
            <Field className="col-6" label="First name" required><input className="d6m-input" placeholder="Enter first name" value={dependantForm.firstName} onChange={e => setDependantForm(p => ({ ...p, firstName: e.target.value }))} /></Field>
            <Field className="col-6" label="Last name" required><input className="d6m-input" placeholder="Enter last name" value={dependantForm.lastName} onChange={e => setDependantForm(p => ({ ...p, lastName: e.target.value }))} /></Field>
          </div>
          <div className="row g-4">
            <Field className="col-6" label="Phone"><input className="d6m-input" placeholder="e.g. 08012345678" value={dependantForm.phoneNumber} onChange={e => setDependantForm(p => ({ ...p, phoneNumber: e.target.value }))} /></Field>
            <Field className="col-6" label="Email (optional)"><input className="d6m-input" placeholder="email@example.com" value={dependantForm.email} onChange={e => setDependantForm(p => ({ ...p, email: e.target.value }))} /></Field>
          </div>
          <Field label="Relationship / role">
            <select className="d6m-input" value={dependantForm.dependentType} onChange={e => setDependantForm(p => ({ ...p, dependentType: Number(e.target.value) }))}>
              <option value={0}>Family Member</option>
              <option value={1}>Staff</option>
              <option value={2}>Child</option>
              <option value={3}>Domestic Staff</option>
              <option value={4}>Driver</option>
              <option value={5}>Visitor (No Access Code)</option>
            </select>
          </Field>

          <div className="d6m-invoice">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="d6m-v">Assign an access code immediately</div>
                <div className="d6m-muted">If turned off, you can generate a code later from their profile.</div>
              </div>
              <div className="form-check form-switch m-0">
                <input className="form-check-input cursor-pointer" type="checkbox" id="generateCodeToggle" checked={dependantForm.generateAccessCode} onChange={e => setDependantForm(p => ({ ...p, generateAccessCode: e.target.checked }))} />
              </div>
            </div>
            {dependantForm.generateAccessCode && (
              <div className="mt-4">
                <div className="d6m-seg mb-3">
                  <button type="button" className={dependantForm.isAccessCodePermanent ? "active" : ""} onClick={() => setDependantForm(p => ({ ...p, isAccessCodePermanent: true }))}>Permanent</button>
                  <button type="button" className={!dependantForm.isAccessCodePermanent ? "active" : ""} onClick={() => setDependantForm(p => ({ ...p, isAccessCodePermanent: false }))}>Temporary</button>
                </div>
                {!dependantForm.isAccessCodePermanent && (
                  <div className="row g-3">
                    <Field className="col-6" label="Duration value"><input type="number" className="d6m-input" value={dependantForm.durationValue} min="1" onChange={e => setDependantForm(p => ({ ...p, durationValue: e.target.value }))} /></Field>
                    <Field className="col-6" label="Duration unit">
                      <select className="d6m-input" value={dependantForm.durationUnit} onChange={e => setDependantForm(p => ({ ...p, durationUnit: Number(e.target.value) }))}>
                        <option value={0}>Hours</option>
                        <option value={1}>Days</option>
                        <option value={2}>Weeks</option>
                        <option value={3}>Months</option>
                      </select>
                    </Field>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ModalShell>

      {/* Add Car Modal */}
      <ModalShell
        id="kt_modal_add_car"
        modalRef={addCarModalRef}
        icon="ki-car"
        title="Register New Vehicle"
        onClose={() => hideModal(addCarModalRef)}
        closeDisabled={savingCar}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(addCarModalRef)} disabled={savingCar}>Cancel</button>
            <button type="button" className="btn btn-success d6-mbtn" onClick={handleSaveCar} disabled={savingCar}>
              {savingCar ? <><span className="spinner-border spinner-border-sm me-1"></span>Registering...</> : <><i className="ki-outline ki-check fs-5 me-2 text-white"></i>Register Car</>}
            </button>
          </>
        }
      >
        <div className="d6-card p-3 d-flex align-items-center justify-content-center bg-light border border-gray-200 mb-5" style={{ height: 170 }}>
          <img alt="Car preview" src={carPreviewUrl || "/assets/media/preview-wait.png"} className="mw-100 mh-100 object-fit-contain d-block" onError={e => { e.currentTarget.src = "/assets/media/preview-wait.png"; }} />
        </div>
        <div className="d-flex flex-column gap-4">
          <div className="row g-4">
            <Field className="col-6" label="Make" required>
              <select className="d6m-input" value={carForm.make} onChange={e => setCarForm(p => ({ ...p, make: e.target.value, model: "" }))}>
                <option value="">Select make…</option>
                {CAR_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field className="col-6" label="Model" required>
              <select className="d6m-input" value={carForm.model} onChange={e => setCarForm(p => ({ ...p, model: e.target.value }))} disabled={!carForm.make}>
                <option value="">Select model…</option>
                {(CAR_MODELS[carForm.make] || []).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <div className="row g-4">
            <Field className="col-6" label="Year" required>
              <select className="d6m-input" value={carForm.year} onChange={e => setCarForm(p => ({ ...p, year: e.target.value }))}>
                <option value="">Select year…</option>
                {CAR_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field className="col-6" label="Color">
              <select className="d6m-input" value={carForm.color} onChange={e => setCarForm(p => ({ ...p, color: e.target.value }))}>
                <option value="">Select color…</option>
                {CAR_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="License plate">
            <input className="d6m-input" placeholder="e.g. ABC-123-XY" value={carForm.licensePlate || ""} onChange={e => setCarForm(p => ({ ...p, licensePlate: e.target.value.toUpperCase() }))} />
          </Field>
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head"><span>Assign drivers (optional)</span><span className="d6m-count">{carDrivers.length}</span></div>
          {accessList.length === 0 ? (
            <div className="d6m-empty">No dependants found. Add them in the Access List first.</div>
          ) : (
            <div className="d6m-list" style={{ maxHeight: 260, overflowY: "auto" }}>
              {accessList.map((a, i) => {
                const isSelected = carDrivers.some(d => d.id === a.id);
                const name = toTitleCase(`${a.firstName || ""} ${a.lastName || ""}`.trim()) || toTitleCase(a.name) || "Dependant";
                return (
                  <button type="button" key={a.id || i} className={`d6m-list-row ${isSelected ? "selected" : ""}`} onClick={() => toggleDriverSelection(a, "Permanent")}>
                    <span className="d6m-check" style={isSelected ? { background: "#0EA84A", borderColor: "#0EA84A" } : undefined}>
                      <svg viewBox="0 0 24 24" style={{ opacity: isSelected ? 1 : 0 }}><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                    <Avatar src={a.imageUrl} name={name} size={40} />
                    <span className="min-w-0 flex-grow-1 text-start">
                      <span className="d6m-v d-block text-truncate">{name}</span>
                      <span className="d6m-muted d-block text-truncate">{formatDependentType(a.dependentType || a.relationship)} · {a.phoneNumber || a.phone || "—"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </ModalShell>

    </>
  );
}
